import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { getApiUrl } from '../utils/api';
import CreateExecutionWizard from './CreateExecutionWizard';
import CycleCasesModal from './modals/CycleCasesModal';
import NewTestCaseModal from './modals/NewTestCaseModal';
import NewTestCycleModal from './modals/NewTestCycleModal';
import NewFolderModal from './modals/NewFolderModal';
import MoveTestCasesModal from './modals/MoveTestCasesModal';
import MoveFoldersModal from './modals/MoveFoldersModal';
import RenameFolderModal from './modals/RenameFolderModal';
import DeleteFolderModal from './modals/DeleteFolderModal';
import EditTestCaseModal from './modals/EditTestCaseModal';
import EditTestCycleModal from './modals/EditTestCycleModal';
import DeleteTestCycleModal from './modals/DeleteTestCycleModal';
import TestPlans from './TestPlans';
import './TestRepository.css';

// Utilitários puros de árvore de pastas — fora do componente de propósito:
// não fecham sobre state, então não entram na cascata de dependências dos
// useCallback que os usam (evita "muda a cada render" no exhaustive-deps).
const findFolderInTree = (folders, targetId) => {
  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (node.id === targetId) return node;
      if (node.children && node.children.length) {
        const res = walk(node.children);
        if (res) return res;
      }
    }
    return null;
  };
  return walk(folders);
};

// Ids de todas as subpastas (recursivo), sem incluir a própria pasta — a
// contagem que aparece do lado do nome já soma tudo isso (ver comentário
// "Pasta-pai soma os cenários das subpastas" no backend); a listagem de
// casos/ciclos precisa bater com o que esse número promete.
const getDescendantFolderIds = (folders, folderId) => {
  const root = findFolderInTree(folders, folderId);
  if (!root) return [];
  const ids = [];
  const walk = (node) => {
    (node.children || []).forEach((child) => {
      ids.push(child.id);
      walk(child);
    });
  };
  walk(root);
  return ids;
};

// Escopo de pastas pra buscar casos/ciclos de uma pasta: ela mesma + toda a
// árvore abaixo dela. null (pasta raiz/MAIN) continua significando "sem
// filtro de pasta" — já traz tudo do projeto.
const folderScopeFor = (folders, folder) => (folder ? [folder.id, ...getDescendantFolderIds(folders, folder.id)] : null);

const TestRepository = () => {
  
  const location = useLocation();
  const { showError, showSuccess } = useNotification();
  const { user, hasPermission } = useAuth();
  const tutorial = useTutorial();
  const contentBodyRef = useRef(null);
  const horizontalScrollRef = useRef(null);
  const searchInputRef = useRef(null);
  const [horizontalScrollWidth, setHorizontalScrollWidth] = useState(1250);
  // Criação de pasta permitida apenas para QA / QA Líder / Admin
  const canCreateFolder = hasPermission('folders.create', ['ADM', 'ADMIN', 'QA', 'TRIAL', 'GERENCIAL']);
  // Edição de caso/ciclo permitida para QA/ADM/ADMIN/TRIAL/UNITARIO
  const canEditCase = hasPermission('testcases.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO']);
  const canEditCycle = hasPermission('executions.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO']);
  const canCreateExecutionFromCycle = hasPermission('executions.create', ['ADM', 'ADMIN', 'QA', 'TRIAL', 'GERENCIAL']);

  // Ir direto para a execução (já existente) de um ciclo, ou abrir a criação
  // de execução pré-preenchida com este ciclo — tudo dentro da própria página
  // do Repositório de Testes, sem navegar pra Execuções e sem modal.
  const [cycleInlineView, setCycleInlineView] = useState(null); // { mode: 'create', cycle } | { mode: 'execute', cycleId, executionRunId, cycleName } | { mode: 'edit', cycle } | { mode: 'newCycle' }
  const [caseInlineView, setCaseInlineView] = useState(null); // { mode: 'newCase' } | { mode: 'edit', testCase }
  const openCycleInline = (cycle) => {
    if (cycle?.execution_run_id) {
      setCycleInlineView({ mode: 'execute', cycleId: cycle.id, executionRunId: cycle.execution_run_id, cycleName: cycle.name });
    } else {
      setCycleInlineView({ mode: 'create', cycle });
    }
  };

  // Menu de ações (⋮) por linha da tabela de ciclos — reúne o "Editar" ali,
  // como no design. Renderizado via portal (document.body) com posição fixa,
  // pra não ficar cortado pelo scroll/overflow da tabela nas últimas linhas.
  const [openCycleMenuId, setOpenCycleMenuId] = useState(null);
  const [cycleMenuPosition, setCycleMenuPosition] = useState(null);
  const toggleCycleMenu = (cycle, buttonEl) => {
    if (openCycleMenuId === cycle.id) {
      setOpenCycleMenuId(null);
      setCycleMenuPosition(null);
      return;
    }
    const rect = buttonEl.getBoundingClientRect();
    setCycleMenuPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right
    });
    setOpenCycleMenuId(cycle.id);
  };
  useEffect(() => {
    if (!openCycleMenuId) return;
    const closeMenu = () => { setOpenCycleMenuId(null); setCycleMenuPosition(null); };
    const handleClickOutside = (e) => {
      if (!e.target.closest('.cycle-action-menu-wrapper') && !e.target.closest('.cycle-action-menu')) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    // Fecha ao rolar (a posição fixa não acompanha o scroll da tabela)
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [openCycleMenuId]);

  // Mesma coisa para a tabela de Casos de Teste
  const [openCaseMenuId, setOpenCaseMenuId] = useState(null);
  const [caseMenuPosition, setCaseMenuPosition] = useState(null);
  const toggleCaseMenu = (testCase, buttonEl) => {
    if (openCaseMenuId === testCase.id) {
      setOpenCaseMenuId(null);
      setCaseMenuPosition(null);
      return;
    }
    const rect = buttonEl.getBoundingClientRect();
    setCaseMenuPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right
    });
    setOpenCaseMenuId(testCase.id);
  };
  useEffect(() => {
    if (!openCaseMenuId) return;
    const closeMenu = () => { setOpenCaseMenuId(null); setCaseMenuPosition(null); };
    const handleClickOutside = (e) => {
      if (!e.target.closest('.cycle-action-menu-wrapper') && !e.target.closest('.cycle-action-menu')) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [openCaseMenuId]);
  
  // Estados principais
  const [activeTab, setActiveTab] = useState('cases'); // 'cases' ou 'cycles'
  // Filtro de arquivamento independente por aba — trocar de aba não deve
  // fazer o filtro de uma "vazar" pra outra sem o usuário perceber.
  const [caseArchivedFilter, setCaseArchivedFilter] = useState('active'); // 'active' | 'archived'
  const [cycleArchivedFilter, setCycleArchivedFilter] = useState('active');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedProject, setSelectedProject] = useState(''); // Novo estado para projeto selecionado
  const [projects, setProjects] = useState([]); // Novo estado para lista de projetos
  const [folders, setFolders] = useState([]);

  // Escopo de pasta (ela + subpastas) pra buscar casos/ciclos — memoizado
  // porque é referenciado dentro de vários useCallback mais abaixo
  // (exclusão, duplicação, arquivar em massa etc.) e precisa de identidade
  // estável pra não invalidar esses hooks a cada render.
  const currentFolderScope = useCallback(
    () => folderScopeFor(folders, selectedFolder),
    [folders, selectedFolder]
  );

  const [testCases, setTestCases] = useState([]);
  const [testCycles, setTestCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isImportingCsv, setIsImportingCsv] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [jiraBaseUrl, setJiraBaseUrl] = useState('');
  const [silentLoad, setSilentLoad] = useState(true);
  // DnD state
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  
  // Estados dos modais
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [showDeleteCycleModal, setShowDeleteCycleModal] = useState(false);
  const [pendingDeletionIds, setPendingDeletionIds] = useState([]);
  const [contextFolder, setContextFolder] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const [quickCreate, setQuickCreate] = useState({ visible: false, x: 0, y: 0, name: '' });
  
  // Estados de filtros
  const [filters] = useState({
    status: '',
    responsible: '',
    priority: ''
  });
  
  // Ordenação da tabela de Ciclo de Testes (Título, Data de Criação, Responsável)
  const [cycleSort, setCycleSort] = useState({ field: null, direction: 'asc' });
  const handleSortCycles = (field) => {
    // Ciclo de 3 estados por coluna: direção padrão -> direção oposta -> sem ordenação.
    const defaultDir = field === 'created_at' ? 'desc' : 'asc';
    const oppositeDir = defaultDir === 'asc' ? 'desc' : 'asc';
    setCycleSort(prev => {
      if (prev.field !== field) return { field, direction: defaultDir };
      if (prev.direction === defaultDir) return { field, direction: oppositeDir };
      return { field: null, direction: 'asc' };
    });
  };
  const clearCycleSort = () => setCycleSort({ field: null, direction: 'asc' });
  const cycleSortIndicator = (field) => (cycleSort.field === field ? (cycleSort.direction === 'asc' ? ' ▲' : ' ▼') : '');

  // Ordenação da tabela de Casos de Teste (Título, Data de Criação, Responsável)
  const [caseSort, setCaseSort] = useState({ field: null, direction: 'asc' });
  const handleSortCases = (field) => {
    // Ciclo de 3 estados por coluna: direção padrão -> direção oposta -> sem ordenação.
    const defaultDir = field === 'created_at' ? 'desc' : 'asc';
    const oppositeDir = defaultDir === 'asc' ? 'desc' : 'asc';
    setCaseSort(prev => {
      if (prev.field !== field) return { field, direction: defaultDir };
      if (prev.direction === defaultDir) return { field, direction: oppositeDir };
      return { field: null, direction: 'asc' };
    });
  };
  const clearCaseSort = () => setCaseSort({ field: null, direction: 'asc' });
  const caseSortIndicator = (field) => (caseSort.field === field ? (caseSort.direction === 'asc' ? ' ▲' : ' ▼') : '');

  // Bolinha de status do caso: reflete o resultado da execução mais recente
  // dele em QUALQUER ciclo (o mesmo cenário pode ser reaproveitado por outras
  // pessoas da empresa em ciclos diferentes) — verde passou, vermelho falhou
  // (bug), amarelo é melhoria. Sem execução ainda (ou só planejado/bloqueado/
  // em andamento), não mostra bolinha.
  const CASE_STATUS_DOT = {
    completed: { color: '#22c55e', label: 'Passou' },
    bug: { color: '#ef4444', label: 'Falhou (bug)' },
    improvement: { color: '#eab308', label: 'Melhoria' }
  };
  const renderCaseStatusDot = (lastExecutionInfo) => {
    const info = CASE_STATUS_DOT[lastExecutionInfo?.status];
    if (!info) return null;
    const when = lastExecutionInfo.executed_at
      ? new Date(lastExecutionInfo.executed_at).toLocaleDateString('pt-BR')
      : null;
    const tooltipLines = [`${info.label} — última execução deste cenário`];
    if (when) tooltipLines.push(`Data: ${when}`);
    if (lastExecutionInfo.run_name) tooltipLines.push(`Ciclo: ${lastExecutionInfo.run_name}`);
    if (lastExecutionInfo.executed_by_name?.trim()) tooltipLines.push(`Por: ${lastExecutionInfo.executed_by_name.trim()}`);
    return (
      <span
        title={tooltipLines.join('\n')}
        style={{
          display: 'inline-block',
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: info.color,
          marginRight: 8,
          flexShrink: 0
        }}
      />
    );
  };

  // Busca e seleção (para fidelidade à imagem)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Seleção múltipla de pastas na árvore lateral (mover/excluir em lote)
  const [selectedFolderIds, setSelectedFolderIds] = useState(new Set());
  const [showMoveFoldersModal, setShowMoveFoldersModal] = useState(false);
  // Seleção total deve considerar a aba ativa (casos ou ciclos)
  const listLength = activeTab === 'cases' ? testCases.length : testCycles.length;
  const allSelected = selectedIds.size > 0 && selectedIds.size === listLength;
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      const ids = activeTab === 'cases'
        ? testCases.map(tc => tc.id)
        : testCycles.map(cy => cy.id);
      setSelectedIds(new Set(ids));
    }
  };
  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openSearch = () => {
    setSearchExpanded(true);
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const closeSearchIfEmpty = () => {
    if (!searchQuery.trim()) {
      setSearchExpanded(false);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Escape' && !searchQuery.trim()) {
      setSearchExpanded(false);
    }
  };

  const renderSearchControl = () => (
    <div className={`search-input ${searchExpanded || searchQuery ? 'expanded' : 'collapsed'}`}>
      <button
        type="button"
        className="search-toggle"
        onClick={openSearch}
        aria-label="Abrir busca"
        title="Buscar"
      >
        🔍
      </button>
      <input
        ref={searchInputRef}
        type="text"
        placeholder="Buscar por nome, ID, responsável..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => setSearchExpanded(true)}
        onBlur={closeSearchIfEmpty}
        onKeyDown={handleSearchKeyDown}
        aria-label="Buscar no repositório"
      />
    </div>
  );

  const syncHorizontalScrollWidth = useCallback(() => {
    const body = contentBodyRef.current;
    if (!body) return;

    const table = body.querySelector('table');
    const width = table ? table.scrollWidth : body.scrollWidth;
    setHorizontalScrollWidth(Math.max(width, body.clientWidth + 1));
  }, []);

  const handleContentBodyScroll = () => {
    if (!contentBodyRef.current || !horizontalScrollRef.current) return;
    horizontalScrollRef.current.scrollLeft = contentBodyRef.current.scrollLeft;
  };

  const handleHorizontalScroll = () => {
    if (!contentBodyRef.current || !horizontalScrollRef.current) return;
    contentBodyRef.current.scrollLeft = horizontalScrollRef.current.scrollLeft;
  };

  const copyToClipboard = async (value) => {
    const text = String(value || '');
    if (!text) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      showSuccess('ID copiado');
    } catch (error) {
      showError('Não foi possível copiar o ID');
    }
  };

  
  // Breadcrumb path
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);
  const [breadcrumbExpanded, setBreadcrumbExpanded] = useState(false);
  // Largura fixa da barra lateral (não é mais redimensionável por arraste)
  const sidebarWidth = 260;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('tr.sidebarCollapsed');
      if (saved === '0') return false;
      if (saved === '1') return true;
    } catch (_) {}
    return true; // mini por padrão
  });
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('tr.sidebarCollapsed', next ? '1' : '0'); } catch (_) {}
      return next;
    });
  };

  useEffect(() => {
    syncHorizontalScrollWidth();
    window.addEventListener('resize', syncHorizontalScrollWidth);
    return () => window.removeEventListener('resize', syncHorizontalScrollWidth);
  }, [activeTab, testCases.length, testCycles.length, selectedProject, sidebarWidth, syncHorizontalScrollWidth]);

  // Função para buscar projetos
  const fetchProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('projects'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data?.projects) ? data.projects : (Array.isArray(data) ? data : []);
        setProjects(list);
      } else {
        console.error('Erro ao carregar projetos');
      }
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
    }
  }, []);

  // Inicializar seleção a partir da query ou última escolha persistida
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const fromQuery = params.get('project_id');
      const fromStorage = localStorage.getItem('selectedProjectId');
      const initial = (fromQuery ?? fromStorage ?? '');
      setSelectedProject(initial);
      // Voltar direto para a aba Ciclo de Testes (ex.: ao sair da execução inline)
      if (params.get('tab') === 'cycles') {
        setActiveTab('cycles');
      }
    } catch (_) {}
    // Executa apenas na montagem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persistir seleção de projeto
  useEffect(() => {
    try {
      localStorage.setItem('selectedProjectId', selectedProject || '');
    } catch (_) {}
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      setSilentLoad(false);
    }
  }, [selectedProject]);

  // Trocar de projeto fecha qualquer execução/edição inline aberta,
  // já que ela pertence ao ciclo/caso do projeto anterior.
  useEffect(() => {
    setCycleInlineView(null);
    setCaseInlineView(null);
  }, [selectedProject]);

  // Carregar dados iniciais
  const fetchFolders = useCallback(async () => {
    try {
      if (!selectedProject) { setFolders([]); return; }
      const token = localStorage.getItem('token');
      let url = 'test-repository/folders';
      
      // Adicionar filtro por projeto se selecionado
      const params = [];
      if (selectedProject) {
        params.push(`project_id=${selectedProject}`);
      }
      // Escopo: 'cases' ou 'cycles' conforme aba ativa
      params.push(`scope=${activeTab}`);
      params.push(`_=${Date.now()}`);
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      const response = await fetch(getApiUrl(url), {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Aceitar tanto { data: [...] } quanto array direto
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        setFolders(list);
      } else if (response.status === 403) {
        // Sem permissão para listar pastas: manter silencioso
        // Não mostrar erro para não interromper criação
        // Mantém estado atual para evitar piscar
      } else {
        if (!silentLoad) showError('Erro ao carregar pastas do repositório');
      }
    } catch (error) {
      console.error('Erro ao buscar pastas:', error);
      if (!silentLoad) showError('Erro ao carregar pastas do repositório');
    }
  }, [showError, selectedProject, activeTab, silentLoad]);

  const fetchTestCases = useCallback(async (folderId = null) => {
    try {
      if (!selectedProject) { setTestCases([]); return; }
      const token = localStorage.getItem('token');
      let url = 'test-repository/test-cases';
      const params = [];
      
      if (folderId) {
        // Aceita um único id ou uma lista (seleção de múltiplas pastas via checkbox)
        const folderIdParam = Array.isArray(folderId) ? folderId.filter(Boolean).join(',') : folderId;
        if (folderIdParam) params.push(`folder_id=${folderIdParam}`);
      }

      // Adicionar filtro por projeto se selecionado
      if (selectedProject) {
        params.push(`project_id=${selectedProject}`);
      }
      params.push(`archived=${caseArchivedFilter}`);
      params.push(`_=${Date.now()}`);

      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      const response = await fetch(getApiUrl(url), {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        if (tutorial?.isTutorialMode) {
          const simulatedList = tutorial?.getSimulatedTestCases?.(selectedProject) || [];
          const byId = new Map();
          [...simulatedList, ...list].forEach((tc) => {
            if (!tc) return;
            byId.set(String(tc.id), tc);
          });
          setTestCases(Array.from(byId.values()));
        } else {
          setTestCases(list);
        }
      } else if (response.status === 403) {
        // Sem permissão para listar casos: ocultar sem erro visual
        setTestCases([]);
      } else {
        if (!silentLoad) showError('Erro ao carregar casos de teste');
      }
    } catch (error) {
      console.error('Erro ao buscar casos de teste:', error);
      if (!silentLoad) showError('Erro ao carregar casos de teste');
    }
  }, [showError, selectedProject, caseArchivedFilter, silentLoad, tutorial]);

  useEffect(() => {
    if (!selectedProject && Array.isArray(projects) && projects.length > 0) {
      const first = projects[0]?.id;
      if (first) {
        setSelectedProject(String(first));
      }
    }
  }, [projects, selectedProject]);

  // Garantir que o projeto selecionado permaneça válido para o usuário atual.
  // Isso evita falhas de criação quando há um project_id legado no localStorage.
  useEffect(() => {
    if (!Array.isArray(projects) || projects.length === 0) return;
    if (!selectedProject) return;
    const stillAccessible = projects.some((p) => String(p.id) === String(selectedProject));
    if (stillAccessible) return;

    const fallbackProjectId = String(projects[0]?.id || '');
    setSelectedProject(fallbackProjectId);
    setSelectedFolder(null);
    setBreadcrumbPath([]);
  }, [projects, selectedProject]);

  // Permissão para excluir ciclos
  const canDeleteCycle = (
    hasPermission('executions.delete', ['ADM', 'ADMIN', 'QA', 'UNITARIO']) ||
    hasPermission('testcases.delete', ['ADM', 'ADMIN', 'QA', 'UNITARIO'])
  );
  const roleUpper = String(user?.role || '').toUpperCase();
  const isIndividualAccount = roleUpper === 'TRIAL' || roleUpper === 'UNITARIO';
  const currentUserId = user?.id || user?.userId;
  const canRequestDeleteCases = hasPermission('testcases.delete', ['ADM', 'ADMIN', 'QA', 'UNITARIO']) || isIndividualAccount;
  const canDeleteAnyCase = ['ADM', 'ADMIN'].includes(roleUpper);
  const canDeleteAnyCycle = ['ADM', 'ADMIN'].includes(roleUpper);
  const canDeleteSelectedCycles = Array.from(selectedIds || new Set()).every((id) => {
    if (canDeleteAnyCycle) return true;
    const cycle = (testCycles || []).find(c => c.id === id);
    if (!cycle) return false;
    return String(cycle.created_by) === String(user?.id);
  });

  // Excluir ciclos selecionados (definido após fetchTestCycles para evitar TDZ)
  // Verificar se casos selecionados pertencem ao usuário atual (responsável)
  const canDeleteSelectedCases = Array.from(selectedIds || new Set()).every((id) => {
    if (canDeleteAnyCase) return true;
    const tc = (testCases || []).find(t => t.id === id);
    if (!tc) return false;
    // Backend lista 'created_by'; tratamos como responsável do cenário
    return String(tc.created_by) === String(currentUserId);
  });

  // Estado para confirmação e relatório detalhado de exclusão
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePendingIds, setDeletePendingIds] = useState([]);
  const [showDeleteResults, setShowDeleteResults] = useState(false);
  const [deleteResults, setDeleteResults] = useState({ successes: [], failures: [] });
  

  const fetchTestCycles = useCallback(async (folderId) => {
    try {
      if (!selectedProject) { setTestCycles([]); return; }
      const token = localStorage.getItem('token');
      let url = 'test-repository/test-cycles';
      
      // Adicionar filtro por projeto se selecionado
      const params = [];
      if (selectedProject) {
        params.push(`project_id=${selectedProject}`);
      }
      if (folderId) {
        // Aceita um único id ou uma lista (pasta pai + descendentes)
        const folderIdParam = Array.isArray(folderId) ? folderId.filter(Boolean).join(',') : folderId;
        if (folderIdParam) params.push(`folder_id=${folderIdParam}`);
      }
      params.push(`archived=${cycleArchivedFilter}`);
      params.push(`_=${Date.now()}`);
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      const response = await fetch(getApiUrl(url), {
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        if (tutorial?.isTutorialMode) {
          const simulatedList = tutorial?.getSimulatedTestCycles?.(selectedProject) || [];
          const byId = new Map();
          [...simulatedList, ...list].forEach((c) => {
            if (!c) return;
            byId.set(String(c.id), c);
          });
          setTestCycles(Array.from(byId.values()));
        } else {
          setTestCycles(list);
        }
      } else if (response.status === 403) {
        // Sem permissão para listar ciclos: não interromper a UI, apenas ocultar
        setTestCycles([]);
        // opcional: manter silencioso para evitar ruído visual
      } else {
        if (!silentLoad) showError('Erro ao carregar ciclos de teste');
      }
    } catch (error) {
      console.error('Erro ao buscar ciclos de teste:', error);
      if (!silentLoad) showError('Erro ao carregar ciclos de teste');
    }
  }, [showError, selectedProject, cycleArchivedFilter, silentLoad, tutorial]);

  const fetchJiraBaseUrl = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl('integrations/jira-basic/config'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` } });
      const json = await res.json().catch(() => ({}));
      const url = json?.data?.url || '';
      setJiraBaseUrl(url || '');
    } catch (_) {
      setJiraBaseUrl('');
    }
  }, []);

  // useEffect para carregar dados iniciais
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchProjects(),
        fetchFolders(),
        fetchTestCases(),
        fetchTestCycles()
      ]);
      setLoading(false);
    };

    loadData();
  }, [fetchProjects, fetchFolders, fetchTestCases, fetchTestCycles]);

  // useEffect para recarregar dados quando o projeto selecionado mudar
  useEffect(() => {
    if (projects.length > 0) {
      // Resetar seleção de pasta quando mudar de projeto
      setSelectedFolder(null);
      setBreadcrumbPath([]);
      
      // Recarregar dados filtrados por projeto
      fetchFolders();
      fetchTestCases();
      fetchTestCycles();
    }
  }, [selectedProject, projects.length, fetchFolders, fetchTestCases, fetchTestCycles]);

  // useEffect para recarregar dados quando a aba ativa mudar
  useEffect(() => {
    if (projects.length > 0) {
      // Resetar seleção de pasta quando mudar de aba
      setSelectedFolder(null);
      setBreadcrumbPath([]);
      // Limpar seleção ao trocar de aba
      setSelectedIds(new Set());
      
      // Recarregar dados filtrados por aba (scope)
      fetchFolders();
      if (activeTab === 'cases') {
        fetchTestCases();
      } else if (activeTab === 'cycles') {
        fetchTestCycles();
        fetchJiraBaseUrl();
      }
    }
  }, [activeTab, projects.length, fetchFolders, fetchTestCases, fetchTestCycles, fetchJiraBaseUrl]);

  // Abrir modal de confirmação para excluir ciclos (com pré-checagem de casos vinculados)
  const promptDeleteSelectedCycles = useCallback(async () => {
    try {
      if (selectedIds.size === 0) return;
      const ids = Array.from(selectedIds).filter(id => (testCycles || []).some(c => c.id === id));
      if (ids.length === 0) return;
      if (!canDeleteSelectedCycles) {
        showError('Somente o responsável pelo ciclo pode excluir os ciclos selecionados');
        return;
      }

      // Pré-checagem: bloquear exclusão se houver casos vinculados
      const token = localStorage.getItem('token');
      const checks = await Promise.all(ids.map(async (id) => {
        try {
          const res = await fetch(getApiUrl(`test-repository/test-cycles/${id}/cases`), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) return { id, hasLinked: false, error: res.statusText };
          const data = await res.json();
          const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
          return { id, hasLinked: Array.isArray(list) && list.length > 0 };
        } catch (e) {
          return { id, hasLinked: false, error: e?.message };
        }
      }));
      const blockedIds = checks.filter(c => c.hasLinked).map(c => c.id);
      if (blockedIds.length > 0) {
        showError('tem casos vinculado a esse ciclo');
        return; // não prosseguir com exclusão
      }
      // Guardar ids e abrir modal de confirmação
      setPendingDeletionIds(ids);
      setShowDeleteCycleModal(true);
    } catch (error) {
      console.error('Erro ao preparar exclusão de ciclos:', error);
      showError('Erro ao preparar exclusão');
    }
  }, [selectedIds, testCycles, canDeleteSelectedCycles, showError]);

  // Excluir ciclos selecionados (executa após confirmação no modal)
  const deleteSelectedCycles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const ids = (pendingDeletionIds && pendingDeletionIds.length > 0)
        ? pendingDeletionIds
        : Array.from(selectedIds).filter(id => (testCycles || []).some(c => c.id === id));
      if (ids.length === 0) return;

      const results = await Promise.all(ids.map(async (id) => {
        try {
          const res = await fetch(getApiUrl(`test-repository/test-cycles/${id}`), {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { id, ok: false, error: err?.error || res.statusText };
          }
          return { id, ok: true };
        } catch (e) {
          return { id, ok: false, error: e?.message || 'Erro desconhecido' };
        }
      }));

      const okIds = results.filter(r => r.ok).map(r => r.id);
      const fail = results.filter(r => !r.ok);

      if (okIds.length > 0) {
        showSuccess(`Excluído${okIds.length > 1 ? 's' : ''}: ${okIds.length}`);
      }
      if (fail.length > 0) {
        // Mostra o motivo real que o backend devolveu, não só a contagem —
        // "Falha: 1" sozinho não dava pra saber o que de fato bloqueou.
        const reasons = Array.from(new Set(fail.map(f => f.error).filter(Boolean)));
        const summary = reasons.length > 0 ? reasons.join(' ') : 'Erro desconhecido';
        showError(fail.length > 1 ? `${fail.length} ciclos não foram excluídos: ${summary}` : summary);
      }

      // Atualizar lista local e recarregar do servidor
      if (okIds.length > 0) {
        setTestCycles(prev => prev.filter(c => !okIds.includes(c.id)));
      }
      setSelectedIds(new Set());
      setPendingDeletionIds([]);
      setShowDeleteCycleModal(false);
      fetchFolders();
      fetchTestCycles(currentFolderScope());
    } catch (error) {
      console.error('Erro ao excluir ciclos:', error);
      showError('Erro ao excluir ciclos');
    }
  }, [pendingDeletionIds, selectedIds, testCycles, currentFolderScope, fetchTestCycles, fetchFolders, showSuccess, showError]);

  // Util para encontrar cadeia de pais de uma pasta dentro da árvore carregada
  const getFolderAncestors = (targetId) => {
    const walk = (nodes, trail = []) => {
      for (const node of nodes || []) {
        const nextTrail = [...trail, node];
        if (node.id === targetId) {
          return trail; // apenas ancestrais, sem o próprio nó selecionado
        }
        if (node.children && node.children.length) {
          const res = walk(node.children, nextTrail);
          if (res) return res;
        }
      }
      return null;
    };
    return walk(folders, []) || [];
  };

  const findFolderById = (targetId) => findFolderInTree(folders, targetId);

  // Quando 2+ pastas são marcadas na árvore (checkbox), carrega na tela os
  // casos de todas elas combinados — antes disso, marcar várias pastas só
  // alimentava a seleção pra ação em massa "Mover", sem refletir na listagem.
  // Com 0 ou 1 marcada, quem manda continua sendo a navegação por clique
  // (handleFolderSelect / selectedFolder), sem mudança de comportamento —
  // exceto ao SAIR do modo múltiplo (desmarcou de volta pra 0/1), quando
  // resincroniza com a pasta única atual pra lista não ficar "grudada"
  // na união anterior.
  const wasMultiFolderRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'cases') return;
    if (selectedFolderIds.size > 1) {
      wasMultiFolderRef.current = true;
      // Cada pasta marcada entra com ela mesma + suas subpastas
      const expanded = Array.from(selectedFolderIds).flatMap((id) => [id, ...getDescendantFolderIds(folders, id)]);
      fetchTestCases(expanded);
    } else if (wasMultiFolderRef.current) {
      // Ainda "em trânsito" saindo do modo múltiplo (pode passar por 1 antes
      // de chegar a 0) — só encerra o modo de fato quando esvazia de vez,
      // senão o passo 1->0 seguinte não resincroniza.
      const remainingId = selectedFolderIds.size === 1 ? Array.from(selectedFolderIds)[0] : null;
      fetchTestCases(remainingId ? folderScopeFor(folders, findFolderById(remainingId)) : currentFolderScope());
      if (selectedFolderIds.size === 0) {
        wasMultiFolderRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolderIds, activeTab, selectedProject, caseArchivedFilter]);

  // Handlers para seleção de pasta
  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    if (activeTab === 'cases') {
      fetchTestCases(folderScopeFor(folders, folder));
    } else if (activeTab === 'cycles') {
      fetchTestCycles(folderScopeFor(folders, folder));
    }

    // Atualizar breadcrumb com fallback seguro quando a API não fornece path
    if (folder) {
      const ancestors = (Array.isArray(folder.path) && folder.path.length)
        ? folder.path
        : getFolderAncestors(folder.id);
      setBreadcrumbPath([...ancestors, folder]);
    } else {
      setBreadcrumbPath([]);
    }

    // Ao navegar, começar em modo compacto por padrão
    setBreadcrumbExpanded(false);
  };

  // Handlers para modais
  const handleNewCase = () => {
    setCaseInlineView({ mode: 'newCase' });
  };

  const handleExportCsv = async () => {
    if (!selectedProject) { showError('Selecione um projeto antes de exportar'); return; }
    try {
      // testCases já vem do backend escopado certo pro que está selecionado
      // (uma pasta + suas subpastas, a união de várias pastas marcadas, ou o
      // projeto inteiro) — exportar sem filtrar de novo aqui, senão volta a
      // descartar os casos das subpastas que acabou de trazer pra tela.
      const casesToExport = testCases;
      if (casesToExport.length === 0) { showError('Nenhum caso para exportar'); return; }

      // Coluna/mensagem de pasta só entram quando o export de fato cruza mais
      // de uma pasta (pasta com subpastas, ou 2+ pastas marcadas) — não
      // quando é uma pasta só sem filhos, ou o projeto inteiro sem seleção.
      const distinctFolderIds = new Set(casesToExport.map(c => c.folder_id || ''));
      const multiFolderMode = distinctFolderIds.size > 1;

      // Criar CSV bem formatado (usa ; para compatibilidade com Excel em locales latinos)
      const escapeField = (field) => {
        const str = String(field || '').trim().replace(/[\r\n]/g, ' ');
        // Se contém ponto-e-vírgula ou aspas, envolve em aspas e escapa aspas internas
        if (str.includes(';') || str.includes('"')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Coluna de Pasta só faz sentido (e só é necessária pra rastrear a
      // origem de cada linha) quando o export cruza mais de uma pasta.
      const headers = multiFolderMode
        ? ['Pasta', 'Título', 'Descrição', 'Pré-condição', 'Passos', 'Resultado Esperado', 'Prioridade']
        : ['Título', 'Descrição', 'Pré-condição', 'Passos', 'Resultado Esperado', 'Prioridade'];
      const csvLines = [
        headers.join(';'),
        ...casesToExport.map(c => [
          ...(multiFolderMode ? [escapeField(c.folder_name || findFolderById(c.folder_id)?.name || 'Sem pasta')] : []),
          escapeField(c.title),
          escapeField(c.description),
          escapeField(c.preconditions),
          escapeField(c.steps),
          escapeField(c.expected_result),
          escapeField(c.priority || 'medium')
        ].join(';'))
      ];

      // Adicionar BOM para UTF-8 (faz Excel reconhecer acentos corretamente)
      const bom = '\uFEFF';
      const csv = bom + csvLines.join('\r\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'casos-teste.csv';
      a.click();
      URL.revokeObjectURL(url);

      if (multiFolderMode) {
        // Total por pasta selecionada, pra conferir que nada ficou de fora
        const countByFolder = new Map();
        casesToExport.forEach((c) => {
          const key = c.folder_name || findFolderById(c.folder_id)?.name || 'Sem pasta';
          countByFolder.set(key, (countByFolder.get(key) || 0) + 1);
        });
        const breakdown = Array.from(countByFolder.entries())
          .map(([name, count]) => `${name}: ${count}`)
          .join(', ');
        showSuccess(`${casesToExport.length} caso(s) exportado(s) de ${countByFolder.size} pastas — ${breakdown}`);
      } else {
        showSuccess(`${casesToExport.length} caso(s) exportado(s) com sucesso!`);
      }
    } catch (e) {
      console.error('Erro ao exportar CSV:', e);
      showError('Erro ao exportar arquivo');
    }
  };

  const handleImportCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (isImportingCsv) return;
    if (!selectedProject) { showError('Selecione um projeto antes de importar'); return; }
    setIsImportingCsv(true);
    try {
      const decodeCsvFile = async (csvFile) => {
        const buffer = await csvFile.arrayBuffer();
        const hasUtf16LeBom = buffer.byteLength >= 2
          && new Uint8Array(buffer.slice(0, 2))[0] === 0xFF
          && new Uint8Array(buffer.slice(0, 2))[1] === 0xFE;
        const hasUtf16BeBom = buffer.byteLength >= 2
          && new Uint8Array(buffer.slice(0, 2))[0] === 0xFE
          && new Uint8Array(buffer.slice(0, 2))[1] === 0xFF;

        if (hasUtf16LeBom || hasUtf16BeBom) {
          return new TextDecoder(hasUtf16LeBom ? 'utf-16le' : 'utf-16be').decode(buffer);
        }

        try {
          return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
        } catch (_) {
          return new TextDecoder('windows-1252').decode(buffer);
        }
      };

      const text = await decodeCsvFile(file);
      const cleanText = text.replace(/^\uFEFF/, '');

      const detectSeparator = (sample) => {
        const candidates = [';', ',', '\t'];
        let best = ',';
        let bestCount = -1;
        candidates.forEach((candidate) => {
          let count = 0;
          let insideQuotes = false;
          for (let i = 0; i < sample.length; i++) {
            const char = sample[i];
            if (char === '"') {
              if (insideQuotes && sample[i + 1] === '"') {
                i++;
              } else {
                insideQuotes = !insideQuotes;
              }
            } else if (char === candidate && !insideQuotes) {
              count++;
            }
          }
          if (count > bestCount) {
            best = candidate;
            bestCount = count;
          }
        });
        return best;
      };

      const firstLine = (cleanText.match(/^[^\r\n]*/) || [''])[0];
      const separator = detectSeparator(firstLine);

      // Parser CSV com suporte a aspas, campos multilinha e quebras CR/LF/CRLF.
      const parseCsv = (input) => {
        const rows = [];
        let row = [];
        let current = '';
        let insideQuotes = false;
        for (let i = 0; i < input.length; i++) {
          const char = input[i];
          if (char === '"') {
            if (insideQuotes && input[i + 1] === '"') {
              current += '"';
              i++;
            } else {
              insideQuotes = !insideQuotes;
            }
          } else if (char === separator && !insideQuotes) {
            row.push(current.trim());
            current = '';
          } else if ((char === '\n' || char === '\r') && !insideQuotes) {
            row.push(current.trim());
            rows.push(row);
            row = [];
            current = '';
            if (char === '\r' && input[i + 1] === '\n') i++;
          } else {
            current += char;
          }
        }
        if (current.length > 0 || row.length > 0) {
          row.push(current.trim());
          rows.push(row);
        }
        return rows.filter(cells => cells.some(cell => String(cell || '').trim() !== ''));
      };

      const rows = parseCsv(cleanText);
      if (rows.length < 2) { showError('Arquivo vazio ou inválido'); return; }

      // Header da primeira linha
      const normalizeHeader = (value) => String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ');
      const headers = rows[0].map(normalizeHeader);
      const findHeaderIndex = (aliases, fallback = -1) => {
        const normalizedAliases = aliases.map(normalizeHeader);
        const idx = headers.findIndex(header => normalizedAliases.includes(header));
        return idx >= 0 ? idx : fallback;
      };

      const titleIdx = findHeaderIndex(['título', 'titulo', 'title', 'nome', 'caso de teste'], 0);
      const descIdx = findHeaderIndex(['descrição', 'descricao', 'description', 'descrição do caso', 'descricao do caso'], 1);
      const precondIdx = findHeaderIndex(['pré-condição', 'pre condicao', 'precondição', 'precondicao', 'precondition', 'preconditions', 'pré-condições', 'pre condicoes']);
      const stepsIdx = findHeaderIndex(['passos', 'steps', 'bdd', 'gherkin', 'cenário bdd', 'cenario bdd', 'cenário', 'cenario', 'roteiro bdd', 'script bdd']);
      const resultIdx = findHeaderIndex(['resultado esperado', 'expected result', 'expected_result', 'resultado', 'então', 'entao']);
      const priorityIdx = findHeaderIndex(['prioridade', 'priority', 'severidade', 'criticidade']);

      const cases = [];
      for (let i = 1; i < rows.length; i++) {
        const cells = rows[i];
        const title = cells[titleIdx]?.trim() || '';
        if (!title) continue;

        cases.push({
          title,
          description: descIdx >= 0 ? cells[descIdx]?.trim() || '' : '',
          preconditions: precondIdx >= 0 ? cells[precondIdx]?.trim() || '' : '',
          steps: stepsIdx >= 0 ? cells[stepsIdx]?.trim() || '' : '',
          expected_result: resultIdx >= 0 ? cells[resultIdx]?.trim() || '' : '',
          priority: (priorityIdx >= 0 ? cells[priorityIdx]?.trim() : '').toLowerCase() || 'medium',
        });
      }

      if (cases.length === 0) { showError('Nenhum caso encontrado no arquivo'); return; }

      const res = await fetch(getApiUrl('test-repository/import-xml'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ cases, project_id: selectedProject, folder_id: selectedFolder?.id || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showError(data?.error || 'Erro ao importar'); return; }
      const failedItems = Array.isArray(data.errors) ? data.errors : [];
      setImportSummary({
        total: cases.length,
        created: Number(data.created) || 0,
        failed: failedItems.length,
        errors: failedItems
      });
      if (failedItems.length === 0) {
        showSuccess(`${data.created} caso(s) importado(s) com sucesso!`);
      } else {
        showSuccess(`${data.created} de ${cases.length} caso(s) importado(s). Verifique o resumo da importação.`);
      }
      await fetchTestCases(currentFolderScope());
    } catch (e) {
      console.error('Erro ao processar arquivo:', e);
      showError('Erro ao processar o arquivo');
    } finally {
      setIsImportingCsv(false);
    }
  };

  const handleNewCycle = () => {
    setCycleInlineView({ mode: 'newCycle' });
  };

  const handleCycleDoubleClick = (cycle) => {
    setCycleInlineView({ mode: 'edit', cycle });
  };

  // Arquivar/desarquivar não é exclusão — diferente do DELETE deste mesmo
  // recurso, não mexe nas execuções vinculadas (sem cascata).
  const handleArchiveCycle = async (cycle) => {
    const isArchived = !!cycle.archived_at;
    const action = isArchived ? 'unarchive' : 'archive';
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`test-repository/test-cycles/${cycle.id}/${action}`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || `Erro ao ${isArchived ? 'desarquivar' : 'arquivar'} ciclo de teste`);
        return;
      }
      showSuccess(isArchived
        ? 'Ciclo de teste desarquivado com sucesso'
        : `Ciclo de teste arquivado com sucesso. Após ${data?.retention_days || 90} dias sem ser desarquivado, será excluído definitivamente.`);
      fetchTestCycles(currentFolderScope());
      fetchFolders();
    } catch (err) {
      console.error('Erro ao arquivar/desarquivar ciclo de teste:', err);
      showError(`Erro ao ${isArchived ? 'desarquivar' : 'arquivar'} ciclo de teste`);
    }
  };

  const handleNewFolder = () => {
    if (!canCreateFolder) {
      showError('Acao nao permitida. Apenas perfis QA/TRIAL/UNITARIO/ADM podem criar pastas no repositorio.');
      return;
    }
    // Ao abrir pelo botão, garantir que não herdamos um contexto de clique direito anterior
    setContextFolder(null);
    setShowNewFolderModal(true);
  };

  const handleCaseDoubleClick = (testCase) => {
    setCaseInlineView({ mode: 'edit', testCase });
  };

  // Arquivar/desarquivar não é exclusão — só recarrega a listagem (que por
  // padrão já exclui arquivados, Fase 2), então o item some da tela como
  // esperado. O registro e tudo que referencia ele continuam intactos.
  const handleArchiveCase = async (testCase) => {
    const isArchived = !!testCase.archived_at;
    const action = isArchived ? 'unarchive' : 'archive';
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`testcases/${testCase.id}/${action}`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || `Erro ao ${isArchived ? 'desarquivar' : 'arquivar'} caso de teste`);
        return;
      }
      showSuccess(isArchived
        ? 'Caso de teste desarquivado com sucesso'
        : `Caso de teste arquivado com sucesso. Após ${data?.retention_days || 90} dias sem ser desarquivado, será excluído definitivamente.`);
      fetchTestCases(currentFolderScope());
      fetchFolders();
    } catch (err) {
      console.error('Erro ao arquivar/desarquivar caso de teste:', err);
      showError(`Erro ao ${isArchived ? 'desarquivar' : 'arquivar'} caso de teste`);
    }
  };

  // Context menu handlers
  const handleContextMenu = (e, folder) => {
    e.preventDefault();
    e.stopPropagation();
    setContextFolder(folder);
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY });
  };

  // Duplicar casos selecionados
  const duplicateSelectedCases = useCallback(async () => {
    try {
      if (selectedIds.size === 0) return;
      const token = localStorage.getItem('token');
      const ids = Array.from(selectedIds);

      // Mapear casos selecionados para dados atuais
      const selectedCases = testCases.filter(tc => ids.includes(tc.id));
      if (selectedCases.length === 0) {
        showError('Nenhum caso válido selecionado para duplicação');
        return;
      }

      const results = await Promise.all(selectedCases.map(async (tc) => {
        try {
          // Incluir folder_id somente se a pasta selecionada pertence ao mesmo projeto do caso
          const body = {};
          if (selectedFolder?.id && String(selectedFolder.project_id) === String(tc.project_id)) {
            body.folder_id = selectedFolder.id;
          }
          const res = await fetch(getApiUrl(`testcases/${tc.id}/clone`), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { ok: false, error: err?.error || `Falha ao clonar #${tc.id}` };
          }
          return { ok: true };
        } catch (err) {
          return { ok: false, error: err?.message || 'Erro desconhecido' };
        }
      }));

      const okCount = results.filter(r => r.ok).length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        showSuccess(`Duplicado${okCount > 1 ? 's' : ''}: ${okCount}`);
      }
      if (failCount > 0) {
        showError(`Falha${failCount > 1 ? 's' : ''} ao duplicar: ${failCount}`);
      }

      // Atualizar lista e limpar seleção
      await fetchTestCases(currentFolderScope());
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Erro na duplicação em massa:', error);
      showError('Erro ao executar duplicação');
    }
  }, [selectedIds, testCases, selectedFolder, currentFolderScope, fetchTestCases, showError, showSuccess]);

  // Duplicar ciclos selecionados
  const duplicateSelectedCycles = useCallback(async () => {
    try {
      if (selectedIds.size === 0) return;
      const token = localStorage.getItem('token');
      const ids = Array.from(selectedIds);

      const selectedCycles = testCycles.filter(cy => ids.includes(cy.id));
      if (selectedCycles.length === 0) {
        showError('Nenhum ciclo válido selecionado para duplicação');
        return;
      }

      const results = await Promise.all(selectedCycles.map(async (cy) => {
        try {
          const body = {};
          if (selectedFolder?.id && String(selectedFolder.project_id) === String(cy.project_id)) {
            body.folder_id = selectedFolder.id;
          }
          const res = await fetch(getApiUrl(`test-repository/test-cycles/${cy.id}/clone`), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { ok: false, error: err?.error || `Falha ao clonar ciclo #${cy.id}` };
          }
          return { ok: true };
        } catch (err) {
          return { ok: false, error: err?.message || 'Erro desconhecido' };
        }
      }));

      const okCount = results.filter(r => r.ok).length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        showSuccess(`${okCount} ciclo(s) clonado(s) com status PLANNED`);
        await fetchTestCycles(currentFolderScope());
        await fetchFolders();
        setSelectedIds(new Set());
      }
      if (failCount > 0) {
        showError(`${failCount} falha(s) ao clonar ciclo(s)`);
      }
    } catch (error) {
      console.error('Erro ao clonar ciclos:', error);
      showError('Erro ao clonar ciclos');
    }
  }, [selectedIds, testCycles, selectedFolder, currentFolderScope, fetchTestCycles, fetchFolders, showSuccess, showError]);

  // Arquivar ciclos selecionados em lote — mesma lógica de arquivar
  // individual, só que chamada para cada item selecionado. Itens já
  // arquivados são pulados (não tratados como falha).
  // Dentro do filtro "Arquivados" a ação em lote deve DESarquivar (tudo ali já
  // está arquivado — chamar /archive nesses itens só devolveria erro "já
  // arquivado" pra cada um). Nos demais filtros ('active'/'all') a ação é
  // arquivar, pulando silenciosamente quem já estiver arquivado (sem contar
  // como falha) — mesma toggle-por-item do menu de três pontinhos por linha.
  const archiveSelectedCycles = useCallback(async () => {
    try {
      if (selectedIds.size === 0) return;
      const token = localStorage.getItem('token');
      const ids = Array.from(selectedIds);
      const isUnarchiveMode = cycleArchivedFilter === 'archived';

      const selectedCycles = (testCycles || []).filter(cy => ids.includes(cy.id));
      if (selectedCycles.length === 0) {
        showError('Nenhum ciclo válido selecionado');
        return;
      }
      const toProcess = selectedCycles.filter(cy => isUnarchiveMode ? !!cy.archived_at : !cy.archived_at);
      const skippedCount = selectedCycles.length - toProcess.length;
      if (toProcess.length === 0) {
        showError(isUnarchiveMode ? 'Nenhum ciclo selecionado está arquivado' : 'Todos os ciclos selecionados já estão arquivados');
        return;
      }

      const action = isUnarchiveMode ? 'unarchive' : 'archive';
      const results = await Promise.all(toProcess.map(async (cy) => {
        try {
          const res = await fetch(getApiUrl(`test-repository/test-cycles/${cy.id}/${action}`), {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            return { ok: false, error: data?.error || `Falha ao ${isUnarchiveMode ? 'desarquivar' : 'arquivar'} ciclo #${cy.id}` };
          }
          return { ok: true, retentionDays: data?.retention_days };
        } catch (err) {
          return { ok: false, error: err?.message || 'Erro desconhecido' };
        }
      }));

      const okCount = results.filter(r => r.ok).length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        const verb = isUnarchiveMode ? 'desarquivado(s)' : 'arquivado(s)';
        const retentionDays = results.find(r => r.ok && r.retentionDays)?.retentionDays || 90;
        const retentionNote = isUnarchiveMode ? '' : ` Após ${retentionDays} dias sem ser desarquivado, cada um será excluído definitivamente.`;
        showSuccess(`${okCount} ciclo(s) de teste ${verb} com sucesso${skippedCount > 0 ? ` (${skippedCount} já estava(m) ${isUnarchiveMode ? 'ativo(s)' : 'arquivado(s)'})` : ''}.${retentionNote}`);
        await fetchTestCycles(currentFolderScope());
        await fetchFolders();
        setSelectedIds(new Set());
      }
      if (failCount > 0) {
        showError(`${failCount} falha(s) ao ${isUnarchiveMode ? 'desarquivar' : 'arquivar'} ciclo(s) de teste`);
      }
    } catch (error) {
      console.error('Erro ao arquivar/desarquivar ciclos selecionados:', error);
      showError('Erro ao processar ciclos selecionados');
    }
  }, [selectedIds, testCycles, cycleArchivedFilter, currentFolderScope, fetchTestCycles, fetchFolders, showSuccess, showError]);

  // Dentro do filtro "Arquivados" a ação em lote deve DESarquivar (tudo ali já
  // está arquivado — chamar /archive nesses itens só devolveria erro "já
  // arquivado" pra cada um). Nos demais filtros ('active'/'all') a ação é
  // arquivar, pulando silenciosamente quem já estiver arquivado (sem contar
  // como falha) — mesma toggle-por-item do menu de três pontinhos por linha.
  const archiveSelectedCases = useCallback(async () => {
    try {
      if (selectedIds.size === 0) return;
      const token = localStorage.getItem('token');
      const ids = Array.from(selectedIds);
      const isUnarchiveMode = caseArchivedFilter === 'archived';

      const selectedCases = (testCases || []).filter(tc => ids.includes(tc.id));
      if (selectedCases.length === 0) {
        showError('Nenhum caso válido selecionado');
        return;
      }
      const toProcess = selectedCases.filter(tc => isUnarchiveMode ? !!tc.archived_at : !tc.archived_at);
      const skippedCount = selectedCases.length - toProcess.length;
      if (toProcess.length === 0) {
        showError(isUnarchiveMode ? 'Nenhum caso selecionado está arquivado' : 'Todos os casos selecionados já estão arquivados');
        return;
      }

      const action = isUnarchiveMode ? 'unarchive' : 'archive';
      const results = await Promise.all(toProcess.map(async (tc) => {
        try {
          const res = await fetch(getApiUrl(`testcases/${tc.id}/${action}`), {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            return { ok: false, error: data?.error || `Falha ao ${isUnarchiveMode ? 'desarquivar' : 'arquivar'} caso #${tc.id}` };
          }
          return { ok: true, retentionDays: data?.retention_days };
        } catch (err) {
          return { ok: false, error: err?.message || 'Erro desconhecido' };
        }
      }));

      const okCount = results.filter(r => r.ok).length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        const verb = isUnarchiveMode ? 'desarquivado(s)' : 'arquivado(s)';
        const retentionDays = results.find(r => r.ok && r.retentionDays)?.retentionDays || 90;
        const retentionNote = isUnarchiveMode ? '' : ` Após ${retentionDays} dias sem ser desarquivado, cada um será excluído definitivamente.`;
        showSuccess(`${okCount} caso(s) de teste ${verb} com sucesso${skippedCount > 0 ? ` (${skippedCount} já estava(m) ${isUnarchiveMode ? 'ativo(s)' : 'arquivado(s)'})` : ''}.${retentionNote}`);
        await fetchTestCases(currentFolderScope());
        await fetchFolders();
        setSelectedIds(new Set());
      }
      if (failCount > 0) {
        showError(`${failCount} falha(s) ao ${isUnarchiveMode ? 'desarquivar' : 'arquivar'} caso(s) de teste`);
      }
    } catch (error) {
      console.error('Erro ao arquivar/desarquivar casos selecionados:', error);
      showError('Erro ao processar casos selecionados');
    }
  }, [selectedIds, testCases, caseArchivedFilter, currentFolderScope, fetchTestCases, fetchFolders, showSuccess, showError]);

  // Exclusão de casos selecionados (com verificação de responsável)
  // Abrir modal de confirmação (não exclui antes de confirmar)
  const openDeleteConfirm = useCallback(() => {
    try {
      if (selectedIds.size === 0) return;
      if (!canRequestDeleteCases) {
        showError('Você não tem permissão para excluir casos de teste');
        return;
      }
      if (!canDeleteSelectedCases) {
        showError('Somente o responsável pelo cenário pode excluir os casos selecionados');
        return;
      }
      const ids = Array.from(selectedIds).filter(id => (testCases || []).some(tc => tc.id === id));
      if (ids.length === 0) return;
      setDeletePendingIds(ids);
      setShowDeleteConfirm(true);
    } catch (error) {
      console.error('Erro ao abrir confirmação de exclusão:', error);
      showError('Erro ao preparar exclusão');
    }
  }, [selectedIds, testCases, canRequestDeleteCases, canDeleteSelectedCases, showError]);

  const cancelDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeletePendingIds([]);
  }, []);

  const deleteSelectedCases = useCallback(async (idsParam, options = {}) => {
    try {
      const token = localStorage.getItem('token');
      const ids = Array.isArray(idsParam) && idsParam.length
        ? idsParam
        : Array.from(selectedIds).filter(id => (testCases || []).some(tc => tc.id === id));
      if (ids.length === 0) return;

      const results = await Promise.all(ids.map(async (id) => {
        try {
          const res = await fetch(getApiUrl(`testcases/${id}`), {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            const reason = err?.error || err?.message || res.statusText || 'Falha desconhecida';
            return { id, ok: false, error: reason };
          }
          return { id, ok: true };
        } catch (e) {
          return { id, ok: false, error: e?.message || 'Erro desconhecido' };
        }
      }));

      const okIds = results.filter(r => r.ok).map(r => r.id);
      const fail = results.filter(r => !r.ok);

      if (okIds.length > 0) {
        showSuccess(`Excluído${okIds.length > 1 ? 's' : ''}: ${okIds.length}`);
      }
      if (fail.length > 0) {
        // Transparência: informar motivo por item
        const detail = fail.map(f => `${f.id}: ${f.error}`).join('; ');
        showError(`Falha${fail.length > 1 ? 's' : ''}: ${fail.length}. Motivos: ${detail}`);
      }

      if (okIds.length > 0) {
        setTestCases(prev => prev.filter(tc => !okIds.includes(tc.id)));
      }
      setSelectedIds(new Set());
      await fetchFolders();
      await fetchTestCases(currentFolderScope());

      const summary = {
        successes: okIds,
        failures: fail
      };
      if (options.returnResults) {
        return summary;
      }
      setDeleteResults(summary);
      setShowDeleteResults(true);
    } catch (error) {
      console.error('Erro ao excluir casos:', error);
      showError('Erro ao excluir casos');
    }
  }, [selectedIds, testCases, currentFolderScope, fetchTestCases, fetchFolders, showSuccess, showError]);

  const confirmDeleteSelectedCases = useCallback(async () => {
    try {
      const results = await deleteSelectedCases(deletePendingIds, { returnResults: true });
      setShowDeleteConfirm(false);
      setDeletePendingIds([]);
      setDeleteResults(results || { successes: [], failures: [] });
      setShowDeleteResults(true);
    } catch (error) {
      console.error('Erro ao confirmar exclusão:', error);
      showError('Erro ao confirmar exclusão');
    }
  }, [deletePendingIds, deleteSelectedCases, showError]);

  // Drag handlers para casos de teste
  const handleCaseDragStart = (e, testCase) => {
    // Bloquear arraste quando não há permissão de atualizar casos (DEV)
    if (!hasPermission('testcases.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO'])) {
      try { e.preventDefault(); } catch (_) {}
      return;
    }
    if (!selectedIds.size) {
      setSelectedIds(new Set([testCase.id]));
    }
    const ids = Array.from(selectedIds.size ? selectedIds : new Set([testCase.id]));
    try {
      e.dataTransfer.setData('application/json', JSON.stringify({ type: 'test-cases', ids }));
    } catch (_) {
      e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'test-cases', ids }));
    }
    e.dataTransfer.effectAllowed = 'move';
    setReorderDragId(testCase.id);
  };

  // Reordenar casos por arrastar-e-soltar dentro da própria lista (linha
  // sobre linha, não sobre uma pasta). Só faz sentido na ordem "padrão",
  // sem filtro/busca/ordenação de coluna ativos.
  const [reorderDragId, setReorderDragId] = useState(null);
  const [reorderOverId, setReorderOverId] = useState(null);
  const isCaseReorderEnabled = !caseSort.field && !filters.status && !filters.responsible && !filters.priority && !searchQuery.trim();

  const handleCaseRowDragOver = (e, testCase) => {
    if (!isCaseReorderEnabled || !reorderDragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (reorderOverId !== testCase.id) setReorderOverId(testCase.id);
  };

  const handleCaseRowDrop = async (e, testCase) => {
    if (!isCaseReorderEnabled || !reorderDragId) return;
    e.preventDefault();
    e.stopPropagation();
    const draggedId = reorderDragId;
    setReorderDragId(null);
    setReorderOverId(null);
    if (draggedId === testCase.id) return;

    const list = [...testCases];
    const fromIndex = list.findIndex(c => c.id === draggedId);
    const toIndex = list.findIndex(c => c.id === testCase.id);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);
    setTestCases(list);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('test-repository/test-cases/reorder'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ordered_ids: list.map(c => c.id) })
      });
      if (!res.ok) throw new Error('Falha ao salvar a ordem');
    } catch (error) {
      console.error('Erro ao reordenar casos:', error);
      showError('Falha ao salvar a nova ordem dos casos');
      fetchTestCases(currentFolderScope());
    }
  };

  const handleCaseRowDragEnd = () => {
    setReorderDragId(null);
    setReorderOverId(null);
  };

  // Drag handlers para ciclos de teste
  const handleCycleDragStart = (e, cycle) => {
    if (!selectedIds.size) {
      setSelectedIds(new Set([cycle.id]));
    }
    const ids = Array.from(selectedIds.size ? selectedIds : new Set([cycle.id]));
    try {
      e.dataTransfer.setData('application/json', JSON.stringify({ type: 'test-cycles', ids }));
    } catch (_) {
      e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'test-cycles', ids }));
    }
    e.dataTransfer.effectAllowed = 'move';
    setCycleReorderDragId(cycle.id);
  };

  // Reordenar ciclos por arrastar-e-soltar dentro da própria lista, igual
  // já existe para os casos de teste. Só ativo na ordem padrão.
  const [cycleReorderDragId, setCycleReorderDragId] = useState(null);
  const [cycleReorderOverId, setCycleReorderOverId] = useState(null);
  const isCycleReorderEnabled = !cycleSort.field && !searchQuery.trim();

  const handleCycleRowDragOver = (e, cycle) => {
    if (!isCycleReorderEnabled || !cycleReorderDragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (cycleReorderOverId !== cycle.id) setCycleReorderOverId(cycle.id);
  };

  const handleCycleRowDrop = async (e, cycle) => {
    if (!isCycleReorderEnabled || !cycleReorderDragId) return;
    e.preventDefault();
    e.stopPropagation();
    const draggedId = cycleReorderDragId;
    setCycleReorderDragId(null);
    setCycleReorderOverId(null);
    if (draggedId === cycle.id) return;

    const list = [...testCycles];
    const fromIndex = list.findIndex(c => c.id === draggedId);
    const toIndex = list.findIndex(c => c.id === cycle.id);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);
    setTestCycles(list);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('test-repository/test-cycles/reorder'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ordered_ids: list.map(c => c.id) })
      });
      if (!res.ok) throw new Error('Falha ao salvar a ordem');
    } catch (error) {
      console.error('Erro ao reordenar ciclos:', error);
      showError('Falha ao salvar a nova ordem dos ciclos');
      fetchTestCycles(currentFolderScope());
    }
  };

  const handleCycleRowDragEnd = () => {
    setCycleReorderDragId(null);
    setCycleReorderOverId(null);
  };

  const moveCasesToFolder = async (caseIds, targetFolderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('test-repository/test-cases/move'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ case_ids: caseIds, target_folder_id: targetFolderId || null })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Falha ao mover casos');
      }
      showSuccess('Casos movidos com sucesso');
      await Promise.all([
        fetchTestCases(currentFolderScope()),
        fetchFolders()
      ]);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Erro ao mover casos:', error);
      showError(error.message || 'Erro ao mover casos');
    }
  };

  const moveFoldersToFolder = async (folderIds, targetFolderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('test-repository/folders/move'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folder_ids: folderIds, target_folder_id: targetFolderId || null })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Falha ao mover pastas');
      }
      showSuccess(folderIds.length > 1 ? 'Pastas movidas com sucesso' : 'Pasta movida com sucesso');
      await fetchFolders();
      setSelectedFolderIds(new Set());
    } catch (error) {
      console.error('Erro ao mover pastas:', error);
      showError(error.message || 'Erro ao mover pastas');
    }
  };

  const handleFolderDragStart = (e, folder) => {
    if (!hasPermission('testcases.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO'])) {
      try { e.preventDefault(); } catch (_) {}
      return;
    }
    const ids = Array.from(selectedFolderIds.size && selectedFolderIds.has(folder.id) ? selectedFolderIds : new Set([folder.id]));
    try {
      e.dataTransfer.setData('application/json', JSON.stringify({ type: 'folders', ids }));
    } catch (_) {
      e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'folders', ids }));
    }
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
  };

  const toggleFolderSelect = (folderId) => {
    setSelectedFolderIds(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const handleFolderDragOver = (e, folder) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folder ? folder.id : 'root');
  };

  const handleFolderDragLeave = () => {
    setDragOverFolderId(null);
  };

  const handleFolderDrop = async (e, folder) => {
    e.preventDefault();
    setDragOverFolderId(null);
    const payloadStr = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
    try {
      const payload = JSON.parse(payloadStr || '{}');
      if (payload.type === 'test-cases' && Array.isArray(payload.ids) && payload.ids.length) {
        if (!hasPermission('testcases.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO'])) {
          showError('Você não tem permissão para mover casos');
          return;
        }
        await moveCasesToFolder(payload.ids, folder ? folder.id : null);
      } else if (payload.type === 'test-cycles' && Array.isArray(payload.ids) && payload.ids.length) {
        if (!hasPermission('executions.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO'])) {
          showError('Você não tem permissão para mover ciclos');
          return;
        }
        await moveCyclesToFolder(payload.ids, folder ? folder.id : null);
      } else if (payload.type === 'folders' && Array.isArray(payload.ids) && payload.ids.length) {
        if (!hasPermission('testcases.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO'])) {
          showError('Você não tem permissão para mover pastas');
          return;
        }
        if (folder && payload.ids.includes(folder.id)) {
          return;
        }
        await moveFoldersToFolder(payload.ids, folder ? folder.id : null);
      }
    } catch (err) {
      console.error('Payload de drop inválido:', err);
    }
  };

  const moveCyclesToFolder = async (cycleIds, targetFolderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('test-repository/test-cycles/move'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cycle_ids: cycleIds, target_folder_id: targetFolderId || null })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Falha ao mover ciclos');
      }
      showSuccess('Ciclos movidos com sucesso');
      await Promise.all([
        fetchTestCycles(currentFolderScope()),
        fetchFolders()
      ]);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Erro ao mover ciclos:', error);
      showError(error.message || 'Erro ao mover ciclos');
    }
  };

  // Clique no corpo da árvore: abrir mini menu de criação
  const handleBodyClickCreate = (e) => {
    const insideInteractive = e.target.closest('.folder-content') || e.target.closest('.context-menu') || e.target.closest('.quick-create-menu') || e.target.closest('.folder-item.root');
    if (insideInteractive) return;
    if (!canCreateFolder) {
      showError('Acao nao permitida. Apenas perfis QA/TRIAL/UNITARIO/ADM podem criar pastas no repositorio.');
      return;
    }
    setQuickCreate({ visible: true, x: e.clientX, y: e.clientY, name: '' });
  };

  // Clique direito no corpo da árvore: abrir mini menu de criação
  const handleBodyContextCreate = (e) => {
    const insideInteractive = e.target.closest('.folder-content') || e.target.closest('.context-menu') || e.target.closest('.quick-create-menu') || e.target.closest('.folder-item.root');
    if (insideInteractive) return;
    e.preventDefault();
    e.stopPropagation();
    if (!canCreateFolder) {
      showError('Acao nao permitida. Apenas perfis QA/TRIAL/UNITARIO/ADM podem criar pastas no repositorio.');
      return;
    }
    setQuickCreate({ visible: true, x: e.clientX, y: e.clientY, name: '' });
  };

  const closeQuickCreate = useCallback(() => {
    setQuickCreate(prev => ({ ...prev, visible: false }));
  }, []);

  const submitQuickCreate = async () => {
    const name = quickCreate.name.trim();
    if (!name) return;
    if (!canCreateFolder) {
      showError('Acao nao permitida. Apenas perfis QA/TRIAL/UNITARIO/ADM podem criar pastas no repositorio.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      let projectIdToUse = null;
      let parentIdToUse = null;

      if (selectedFolder && selectedFolder.project_id) {
        // IDs de projeto são UUIDs; não converter para número
        projectIdToUse = selectedFolder.project_id;
        parentIdToUse = selectedFolder?.id || null;
      } else if (selectedFolder === null) {
        // Raiz (MAIN) selecionada: criar no nível raiz usando o projeto atualmente selecionado
        if (selectedProject) {
          projectIdToUse = selectedProject;
          parentIdToUse = null;
        } else {
          // Fallback: buscar lista e usar o primeiro projeto acessível
          const projRes = await fetch(getApiUrl('projects'), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (projRes.ok) {
            const projData = await projRes.json();
            const list = Array.isArray(projData?.projects) ? projData.projects : (Array.isArray(projData) ? projData : []);
            if (list.length === 0) {
              showError('Nenhum projeto disponível para criar pasta.');
              return;
            }
            projectIdToUse = list[0].id;
            parentIdToUse = null;
          } else {
            showError('Erro ao carregar projetos para criação na raiz.');
            return;
          }
        }
      } else {
        showError('Selecione uma pasta válida ou a raiz MAIN.');
        return;
      }

      const res = await fetch(getApiUrl('test-repository/folders'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          description: null,
          parent_folder_id: parentIdToUse,
          project_id: projectIdToUse,
          // Garantir que criação rápida respeite a aba ativa (cases/cycles)
          scope: activeTab
        })
      });
      if (res.ok) {
        // Tentar parsear a pasta criada; se falhar, segue com fetch
        const created = await res.json().catch(() => null);
        showSuccess('Pasta criada com sucesso');

        // Atualização otimista da árvore
        if (created && created.id) {
          const newNode = {
            ...created,
            children: [],
            test_cases_count: 0,
            test_cycles_count: 0
          };
          setFolders(prev => {
            // Inserir na raiz ou como filho
            const clone = JSON.parse(JSON.stringify(prev || []));
            if (!newNode.parent_folder_id) {
              clone.push(newNode);
              return clone;
            }
            const insertRecursive = (nodes) => {
              for (const n of nodes) {
                if (n.id === newNode.parent_folder_id) {
                  n.children = n.children || [];
                  n.children.push(newNode);
                  return true;
                }
                if (n.children && n.children.length) {
                  const found = insertRecursive(n.children);
                  if (found) return true;
                }
              }
              return false;
            };
            const ok = insertRecursive(clone);
            if (!ok) {
              // Se não achar o pai, coloca na raiz para não perder visibilidade
              clone.push(newNode);
            }
            return clone;
          });
        }

        // Sincronizar com backend (counts e ordem)
        await fetchFolders();
        closeQuickCreate();
      } else {
        const errData = await res.json().catch(() => ({}));
        showError(errData.error || 'Erro ao criar pasta');
      }
    } catch (err) {
      console.error('Erro ao criar pasta (quick):', err);
      showError('Erro ao criar pasta');
    }
  };

  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, []);

  useEffect(() => {
    const onClick = () => { closeContextMenu(); closeQuickCreate(); };
    const onEsc = (e) => { if (e.key === 'Escape') { closeContextMenu(); closeQuickCreate(); } };
    window.addEventListener('click', onClick);
    window.addEventListener('contextmenu', onClick);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('contextmenu', onClick);
      window.removeEventListener('keydown', onEsc);
    };
  }, [closeContextMenu, closeQuickCreate]);

  // Renderizar estrutura em árvore
  const renderFolderTree = (folders, level = 0) => {
    return folders.map(folder => (
      <div key={folder.id} className={`folder-item level-${level} ${folder.children && folder.children.length > 0 ? 'has-children' : ''}`}>
        <div
          className={`folder-content ${selectedFolder?.id === folder.id ? 'selected' : ''} ${dragOverFolderId === folder.id ? 'drag-over' : ''} ${selectedFolderIds.has(folder.id) ? 'checked-for-bulk' : ''}`}
          title={folder?.description || undefined}
          draggable
          onClick={() => handleFolderSelect(folder)}
          onContextMenu={(e) => handleContextMenu(e, folder)}
          onDragStart={(e) => handleFolderDragStart(e, folder)}
          onDragOver={(e) => handleFolderDragOver(e, folder)}
          onDragEnter={(e) => handleFolderDragOver(e, folder)}
          onDragLeave={handleFolderDragLeave}
          onDrop={(e) => handleFolderDrop(e, folder)}
        >
          <input
            type="checkbox"
            className="folder-select-checkbox"
            checked={selectedFolderIds.has(folder.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleFolderSelect(folder.id)}
            title="Selecionar pasta"
          />
          <span className="folder-icon">📁</span>
          <span className="folder-name" title={folder.name}>{folder.name}</span>
          <span className="folder-count">({activeTab === 'cycles' ? (folder.test_cycles_count || 0) : (folder.test_cases_count || 0)})</span>
        </div>
        {folder.children && folder.children.length > 0 && (
          <div className="folder-children">
            {renderFolderTree(folder.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  // Renderizar breadcrumb (em uso)
  const renderBreadcrumb = () => {
    if (breadcrumbPath.length === 0) return <span>Repositório de Testes</span>;
    const total = breadcrumbPath.length;
    const hiddenNames = breadcrumbPath.slice(1, -1).map(f => f.name).join(' / ');

    // Modo compacto: quando há muitos segmentos, colapsa o miolo
    if (!breadcrumbExpanded && total > 4) {
      const first = breadcrumbPath[0];
      const penultimate = breadcrumbPath[total - 2];
      const last = breadcrumbPath[total - 1];
      return (
        <div className="breadcrumb">
          <span
            className="breadcrumb-item clickable"
            onClick={() => handleFolderSelect(null)}
          >
            Repositório
          </span>
          <span className="breadcrumb-separator"> / </span>
          <span
            className="breadcrumb-item clickable"
            title={first.name}
            onClick={() => handleFolderSelect(first)}
          >
            {first.name}
          </span>
          <span className="breadcrumb-separator"> / </span>
          <span
            className="breadcrumb-ellipsis clickable"
            title={hiddenNames || '…'}
            onClick={() => setBreadcrumbExpanded(true)}
          >
            …
          </span>
          <span className="breadcrumb-separator"> / </span>
          <span
            className="breadcrumb-item clickable"
            title={penultimate.name}
            onClick={() => handleFolderSelect(penultimate)}
          >
            {penultimate.name}
          </span>
          <span className="breadcrumb-separator"> / </span>
          <span className="breadcrumb-item" title={last.name}>{last.name}</span>
        </div>
      );
    }

    // Modo expandido (ou poucos segmentos): mostra todos, com título
    return (
      <div className="breadcrumb">
        <span
          className="breadcrumb-item clickable"
          onClick={() => handleFolderSelect(null)}
        >
          Repositório
        </span>
        {breadcrumbPath.map((folder, index) => (
          <React.Fragment key={folder.id}>
            <span className="breadcrumb-separator"> / </span>
            <span
              className={`breadcrumb-item ${index < breadcrumbPath.length - 1 ? 'clickable' : ''}`}
              title={folder.name}
              onClick={() => index < breadcrumbPath.length - 1 ? handleFolderSelect(folder) : null}
            >
              {folder.name}
            </span>
          </React.Fragment>
        ))}
        {total > 4 && (
          <>
            <span className="breadcrumb-separator"> / </span>
            <span
              className="breadcrumb-ellipsis clickable"
              title="Compactar breadcrumb"
              onClick={() => setBreadcrumbExpanded(false)}
            >
              …
            </span>
          </>
        )}
      </div>
    );
  };

  const renderCaseInline = () => {
    if (!caseInlineView) return null;
    if (caseInlineView.mode === 'newCase') {
      return (
        <NewTestCaseModal
          isOpen={true}
          onClose={() => setCaseInlineView(null)}
          onSuccess={() => {
            fetchTestCases(currentFolderScope());
            fetchFolders();
            setCaseInlineView(null);
          }}
          selectedFolder={selectedFolder}
          projectId={selectedProject}
        />
      );
    }
    if (caseInlineView.mode === 'edit') {
      return (
        <EditTestCaseModal
          isOpen={true}
          testCase={caseInlineView.testCase}
          onClose={() => setCaseInlineView(null)}
          onSuccess={() => {
            fetchTestCases(currentFolderScope());
            setCaseInlineView(null);
          }}
          readOnly={!canEditCase || !!caseInlineView.testCase?.archived_at}
        />
      );
    }
    return null;
  };

  // Renderizar casos de teste
  const renderTestCases = () => {
    const filteredCases = testCases.filter(testCase => {
      if (filters.status && testCase.status !== filters.status) return false;
      // Ajuste: usar nome do criador como responsável exibido
      if (filters.responsible && testCase.created_by_name !== filters.responsible) return false;
      if (filters.priority && testCase.priority !== filters.priority) return false;
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        // Busca por ID, título e criador
        const idStr = String(testCase.id || '').toLowerCase();
        const title = String(testCase.title || '').toLowerCase();
        const creator = String(testCase.created_by_name || '').toLowerCase();
        if (!(idStr.includes(q) || title.includes(q) || creator.includes(q))) return false;
      }
      return true;
    });

    if (caseSort.field) {
      const dir = caseSort.direction === 'asc' ? 1 : -1;
      filteredCases.sort((a, b) => {
        if (caseSort.field === 'created_at') {
          return (new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()) * dir;
        }
        const getVal = (testCase) => (caseSort.field === 'title'
          ? String(testCase.title || '').toLowerCase()
          : String(testCase.created_by_name || '').toLowerCase());
        return getVal(a).localeCompare(getVal(b), 'pt-BR') * dir;
      });
    }

    return (
      <div className="test-cases-table">
        <table>
          <thead>
            <tr>
              <th></th>
              <th className="sortable-th" onClick={() => handleSortCases('title')}>Título{caseSortIndicator('title')}</th>
              <th className="sortable-th" onClick={() => handleSortCases('created_at')}>Data de Criação{caseSortIndicator('created_at')}</th>
              <th className="sortable-th" onClick={() => handleSortCases('responsible')}>Responsável{caseSortIndicator('responsible')}</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredCases.map(testCase => (
              <tr
                key={testCase.id}
                className={[
                  reorderOverId === testCase.id ? 'case-row-drag-over' : '',
                  isCaseReorderEnabled ? 'case-row-draggable' : ''
                ].filter(Boolean).join(' ')}
                draggable={hasPermission('testcases.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO'])}
                onDragStart={(e) => handleCaseDragStart(e, testCase)}
                onDragOver={(e) => handleCaseRowDragOver(e, testCase)}
                onDrop={(e) => handleCaseRowDrop(e, testCase)}
                onDragEnd={handleCaseRowDragEnd}
                onDoubleClick={() => handleCaseDoubleClick(testCase)}
                title={isCaseReorderEnabled ? 'Arraste para reordenar' : undefined}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(testCase.id)}
                    onChange={() => toggleSelectOne(testCase.id)}
                  />
                </td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {renderCaseStatusDot(testCase.last_execution_info)}
                    <span
                      className="case-title-link"
                      onClick={() => handleCaseDoubleClick(testCase)}
                      title={testCase.archived_at ? 'Abrir para visualizar' : 'Abrir para editar'}
                    >
                      {testCase.title}
                    </span>
                    {testCase.archived_at && <span className="archived-badge">🗄️ Arquivado</span>}
                  </span>
                </td>
                <td>
                  {testCase.created_at
                    ? new Date(testCase.created_at).toLocaleString('pt-BR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })
                    : '—'}
                </td>
                <td>{testCase.created_by_name || '-'}</td>
                <td style={{ minWidth: '60px', textAlign: 'right' }}>
                  <div className="cycle-action-menu-wrapper">
                    <button
                      className="cycle-action-kebab"
                      onClick={(e) => toggleCaseMenu(testCase, e.currentTarget)}
                      title="Mais ações"
                    >
                      ⋮
                    </button>
                    {openCaseMenuId === testCase.id && caseMenuPosition && createPortal(
                      <div
                        className="cycle-action-menu"
                        style={{ position: 'fixed', top: caseMenuPosition.top, right: caseMenuPosition.right }}
                      >
                        <button
                          onClick={() => { setOpenCaseMenuId(null); handleCaseDoubleClick(testCase); }}
                        >
                          {testCase.archived_at ? '👁️ Visualizar' : '✏️ Editar'}
                        </button>
                        <button
                          onClick={() => { setOpenCaseMenuId(null); copyToClipboard(testCase.id); }}
                        >
                          📋 Copiar ID
                        </button>
                        <button
                          onClick={() => { setOpenCaseMenuId(null); handleArchiveCase(testCase); }}
                        >
                          {testCase.archived_at ? '↩️ Desarquivar' : '🗄️ Arquivar'}
                        </button>
                      </div>,
                      document.body
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Renderizar ciclos de teste como tabela (ID, Nome, Responsável, Data de Criação, Status)
  const renderCycleInline = () => {
    if (!cycleInlineView) return null;
    if (cycleInlineView.mode === 'create') {
      const cycle = cycleInlineView.cycle;
      return (
        <CreateExecutionWizard
          projectId={cycle.project_id}
          projectName={cycle.project_name}
          cycleId={cycle.id}
          cycleName={cycle.name}
          onCancel={() => setCycleInlineView(null)}
          onCreated={(run) => {
            fetchTestCycles(currentFolderScope());
            setCycleInlineView({ mode: 'execute', cycleId: cycle.id, executionRunId: run?.id, cycleName: cycle.name });
          }}
        />
      );
    }
    if (cycleInlineView.mode === 'execute') {
      return (
        <CycleCasesModal
          isOpen={true}
          onClose={() => {
            setCycleInlineView(null);
            fetchTestCycles(currentFolderScope());
          }}
          cycleId={cycleInlineView.cycleId}
          runId={cycleInlineView.cycleId}
          executionId={cycleInlineView.executionRunId}
          isReadOnly={false}
        />
      );
    }
    if (cycleInlineView.mode === 'edit') {
      return (
        <EditTestCycleModal
          isOpen={true}
          cycle={cycleInlineView.cycle}
          onClose={() => setCycleInlineView(null)}
          onSuccess={(updated) => {
            setCycleInlineView(null);
            setTestCycles(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
            fetchTestCycles(currentFolderScope());
          }}
          readOnly={!canEditCycle || !!cycleInlineView.cycle?.archived_at}
        />
      );
    }
    if (cycleInlineView.mode === 'newCycle') {
      return (
        <NewTestCycleModal
          isOpen={true}
          onClose={() => setCycleInlineView(null)}
          onSuccess={() => {
            setCycleInlineView(null);
            fetchTestCycles(currentFolderScope());
          }}
          projectId={selectedProject}
          selectedFolder={selectedFolder}
        />
      );
    }
    return null;
  };

  const renderTestCycles = () => {
    const filteredCycles = (testCycles || []).filter(cycle => {
      const q = (searchQuery || '').trim().toLowerCase();
      if (!q) return true;
      const idStr = String(cycle.id || '').toLowerCase();
      const nameStr = String(cycle.name || '').toLowerCase();
      const respStr = String(cycle.created_by_name || cycle.responsible || '').toLowerCase();
      return idStr.includes(q) || nameStr.includes(q) || respStr.includes(q);
    });

    if (cycleSort.field) {
      const dir = cycleSort.direction === 'asc' ? 1 : -1;
      filteredCycles.sort((a, b) => {
        if (cycleSort.field === 'created_at') {
          return (new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()) * dir;
        }
        const getVal = (cycle) => {
          if (cycleSort.field === 'name') return String(cycle.name || '').toLowerCase();
          if (cycleSort.field === 'status') return String(cycle.status || '').toLowerCase();
          return String(cycle.created_by_name || cycle.responsible || '').toLowerCase();
        };
        return getVal(a).localeCompare(getVal(b), 'pt-BR') * dir;
      });
    }

    return (
      <div className="test-cycles-table">
        <table>
          <thead>
            <tr>
              <th></th>
              <th className="sortable-th" onClick={() => handleSortCycles('name')}>Título{cycleSortIndicator('name')}</th>
              <th className="sortable-th" onClick={() => handleSortCycles('created_at')}>Data de Criação{cycleSortIndicator('created_at')}</th>
              <th className="sortable-th" onClick={() => handleSortCycles('status')}>Status{cycleSortIndicator('status')}</th>
              <th className="sortable-th" onClick={() => handleSortCycles('responsible')}>Responsável{cycleSortIndicator('responsible')}</th>
              <th>Jira Story</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredCycles.map(cycle => (
              <tr
                key={cycle.id}
                className={[
                  cycleReorderOverId === cycle.id ? 'case-row-drag-over' : '',
                  isCycleReorderEnabled ? 'case-row-draggable' : ''
                ].filter(Boolean).join(' ')}
                draggable
                onDragStart={(e) => handleCycleDragStart(e, cycle)}
                onDragOver={(e) => handleCycleRowDragOver(e, cycle)}
                onDrop={(e) => handleCycleRowDrop(e, cycle)}
                onDragEnd={handleCycleRowDragEnd}
                onDoubleClick={() => handleCycleDoubleClick(cycle)}
                title={isCycleReorderEnabled ? 'Arraste para reordenar' : undefined}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(cycle.id)}
                    onChange={() => toggleSelectOne(cycle.id)}
                  />
                </td>
                <td>
                  <span
                    className="case-title-link"
                    onClick={() => handleCycleDoubleClick(cycle)}
                    title={cycle.archived_at ? 'Abrir para visualizar' : 'Abrir para editar'}
                  >
                    {cycle.name}
                  </span>
                  {cycle.archived_at && <span className="archived-badge">🗄️ Arquivado</span>}
                </td>
                <td>
                  {cycle.created_at
                    ? new Date(cycle.created_at).toLocaleString('pt-BR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })
                    : '—'}
                </td>
                <td>
                  <span className={`status-badge status-${String(cycle.status || '').toLowerCase()}`}>
                    {cycle.status || '—'}
                  </span>
                </td>
                <td>{cycle.created_by_name || cycle.responsible || '—'}</td>
                <td>
                  {cycle.jira_story_key ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {cycle.jira_story_key}
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={() => {
                          const v = String(cycle.jira_story_key || '').trim();
                          const isUrl = /^https?:\/\//i.test(v);
                          if (isUrl) {
                            window.open(v, '_blank');
                            return;
                          }
                          const base = jiraBaseUrl?.replace(/\/$/, '') || '';
                          const href = base ? `${base}/browse/${v}` : `https://jira.atlassian.com/browse/${v}`;
                          window.open(href, '_blank');
                        }}
                      >Abrir</button>
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td style={{ minWidth: '60px', textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {(cycle.has_execution || canCreateExecutionFromCycle) && !cycle.archived_at && (
                      <button
                        className="btn-primary btn-sm cycle-exec-btn"
                        onClick={() => openCycleInline(cycle)}
                        title={cycle.has_execution ? 'Executar casos deste ciclo' : 'Criar execução para este ciclo'}
                      >
                        {cycle.has_execution ? '▶️ Executar' : '+ Criar Execução'}
                      </button>
                    )}
                    <div className="cycle-action-menu-wrapper">
                      <button
                        className="cycle-action-kebab"
                        onClick={(e) => toggleCycleMenu(cycle, e.currentTarget)}
                        title="Mais ações"
                      >
                        ⋮
                      </button>
                      {openCycleMenuId === cycle.id && cycleMenuPosition && createPortal(
                        <div
                          className="cycle-action-menu"
                          style={{ position: 'fixed', top: cycleMenuPosition.top, right: cycleMenuPosition.right }}
                        >
                          <button
                            onClick={() => { setOpenCycleMenuId(null); handleCycleDoubleClick(cycle); }}
                          >
                            {cycle.archived_at ? '👁️ Visualizar' : '✏️ Editar'}
                          </button>
                          <button
                            onClick={() => { setOpenCycleMenuId(null); copyToClipboard(cycle.id); }}
                          >
                            📋 Copiar ID
                          </button>
                          <button
                            onClick={() => { setOpenCycleMenuId(null); handleArchiveCycle(cycle); }}
                          >
                            {cycle.archived_at ? '↩️ Desarquivar' : '🗄️ Arquivar'}
                          </button>
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Carregando repositório de testes...</div>;
  }

  return (
    <div className="test-repository">
      <div className="repository-header">
        <div>
          <h1>📚 Repositório de Testes</h1>
          <p>Organize casos, ciclos e pastas por projeto em um único lugar.</p>
        </div>
        <div className="project-selector">
          <label htmlFor="projectSelect" className="project-select-label">Selecionar o Projeto:</label>
          <select
            id="projectSelect"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="project-select"
            data-tour="repo-project-select"
          >
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>
      </div>

        <div className="repository-tabs">
          <div className="tabs-left">
            <button
              className={`tab ${activeTab === 'cases' ? 'active' : ''}`}
              onClick={() => { setActiveTab('cases'); setCycleInlineView(null); setCaseInlineView(null); }}
            >
              Casos de testes
            </button>
            <button
              className={`tab ${activeTab === 'cycles' ? 'active' : ''}`}
              onClick={() => { setActiveTab('cycles'); setCycleInlineView(null); setCaseInlineView(null); }}
              data-tour="aba-ciclos"
            >
              Ciclo de Testes
            </button>
            <button
              className={`tab ${activeTab === 'plans' ? 'active' : ''}`}
              onClick={() => { setActiveTab('plans'); setCycleInlineView(null); setCaseInlineView(null); }}
              data-tour="aba-planos"
            >
              Planos de Teste
            </button>
          </div>
        </div>

      {activeTab === 'plans' ? (
        selectedProject ? (
          <TestPlans projectId={selectedProject} />
        ) : (
          <div className="repository-content">
            <div className="main-content">
              <div className="content-body">
                <div className="empty-state">Selecione um projeto para visualizar os planos de teste.</div>
              </div>
            </div>
          </div>
        )
      ) : selectedProject ? (
      <div className="repository-content">
        <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`} style={{ width: sidebarCollapsed ? 40 : sidebarWidth }}>
          <div className="sidebar-header">
            <button
              className="btn-icon sidebar-collapse-toggle"
              onClick={toggleSidebarCollapsed}
              title={sidebarCollapsed ? 'Mostrar estrutura de pastas' : 'Esconder estrutura de pastas'}
            >
              {sidebarCollapsed ? '»' : '«'}
            </button>
            {!sidebarCollapsed && <h3>Estrutura</h3>}
            {!sidebarCollapsed && canCreateFolder && (
              <button
                className="btn-icon"
                onClick={handleNewFolder}
                title="Nova Pasta"
              >
                📁+
              </button>
            )}
          </div>
          {/* Texto informativo removido da aba Ciclo de Testes */}
          {!sidebarCollapsed && (
            <>
      {selectedFolderIds.size > 0 && (
        <div className="folder-bulk-toolbar">
          <span>
            {selectedFolderIds.size} pasta{selectedFolderIds.size > 1 ? 's' : ''} selecionada{selectedFolderIds.size > 1 ? 's' : ''}
            {activeTab === 'cases' && selectedFolderIds.size > 1 && ` · ${testCases.length} caso(s) no total`}
          </span>
          <div className="folder-bulk-actions">
            {hasPermission('testcases.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO']) && (
              <button type="button" className="btn-link" onClick={() => setShowMoveFoldersModal(true)}>Mover</button>
            )}
          </div>
        </div>
      )}
      <div className="folder-tree" onClick={handleBodyClickCreate} onContextMenu={handleBodyContextCreate}>
        <div
          className={`folder-item root ${!selectedFolder ? 'selected' : ''} ${dragOverFolderId === 'root' ? 'drag-over' : ''}`}
          onClick={() => handleFolderSelect(null)}
          onContextMenu={(e) => handleContextMenu(e, null)}
          onDragOver={(e) => handleFolderDragOver(e, null)}
          onDragEnter={(e) => handleFolderDragOver(e, null)}
          onDragLeave={handleFolderDragLeave}
          onDrop={(e) => handleFolderDrop(e, null)}
        >
          <span className="folder-icon">🏠</span>
          <span className="folder-name">MAIN</span>
        </div>
        <div className="folder-children root-children">
          {renderFolderTree(folders)}
        </div>
      </div>
      {contextMenu.visible && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x, position: 'fixed' }}
        >
          <div className={`context-menu-item ${!canCreateFolder ? 'disabled' : ''}`} onClick={() => { if (!canCreateFolder) return; closeContextMenu(); setShowNewFolderModal(true); }}>
            {contextFolder ? 'Criar Subpasta' : 'Nova pasta'}
          </div>
          <div className={`context-menu-item ${!contextFolder ? 'disabled' : ''}`} onClick={() => { if (!contextFolder) return; closeContextMenu(); setShowRenameFolderModal(true); }}>
            Editar
          </div>
          <div className={`context-menu-item danger ${!contextFolder ? 'disabled' : ''}`} onClick={() => { if (!contextFolder) return; closeContextMenu(); setShowDeleteFolderModal(true); }}>
            Excluir
          </div>
        </div>
      )}

      {quickCreate.visible && (
        <div
          className="quick-create-menu"
          style={{ top: quickCreate.y, left: quickCreate.x, position: 'fixed' }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          {(selectedFolder === null || selectedFolder?.project_id) ? (
            <div className="quick-create-row">
              <input
                type="text"
                placeholder="Nome da nova pasta"
                value={quickCreate.name}
                onChange={(e) => setQuickCreate(prev => ({ ...prev, name: e.target.value }))}
                autoFocus
              />
              <button className="btn btn-primary" onClick={submitQuickCreate}>Criar</button>
              <button className="btn btn-secondary" onClick={closeQuickCreate}>Cancelar</button>
            </div>
          ) : (
            <div className="quick-create-row">
              <span className="quick-create-hint">Selecione uma pasta válida para criar aqui</span>
              <button className="btn btn-secondary" onClick={closeQuickCreate}>Fechar</button>
            </div>
          )}
        </div>
      )}
            </>
          )}
        </div>

        <div className="main-content">
          <div className="content-header">
            {!((activeTab === 'cycles' && cycleInlineView) || (activeTab === 'cases' && caseInlineView)) && (
              <div className="breadcrumb-container">
                {renderBreadcrumb()}
                <div className="breadcrumb-search">
                  {renderSearchControl()}
                </div>
              </div>
            )}

            {activeTab === 'cases' && !caseInlineView && (
              <div className="table-toolbar">
                <div className="table-toolbar-left">
                  <select
                    className="archived-filter-select"
                    value={caseArchivedFilter}
                    onChange={(e) => setCaseArchivedFilter(e.target.value)}
                    title="Filtrar por arquivamento"
                  >
                    <option value="active">Ativos</option>
                    <option value="archived">Arquivados</option>
                  </select>
                  <span>|</span>
                  <label className="select-all">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                    Selecionar tudo
                  </label>
                  <span>|</span>
                  <span>Selecionados: {selectedIds.size}</span>
                  <span>|</span>
                  <span className="case-status-legend" title="A bolinha mostra o resultado da última vez que o cenário foi executado, em qualquer ciclo">
                    <span className="case-status-legend-dot" style={{ background: '#22c55e' }} /> Passou
                    <span className="case-status-legend-dot" style={{ background: '#ef4444' }} /> Falhou
                    <span className="case-status-legend-dot" style={{ background: '#eab308' }} /> Melhoria
                    <span className="muted" style={{ marginLeft: 4 }}>(última execução)</span>
                  </span>
                  {caseSort.field && (
                    <>
                      <span>|</span>
                      <button
                        type="button"
                        className="btn-link"
                        onClick={clearCaseSort}
                        title="Voltar para a ordem padrão (permite arrastar para reordenar)"
                      >
                        ✕ Remover ordenação
                      </button>
                    </>
                  )}
                </div>
                <div className="table-toolbar-right">
                  <div className="table-toolbar-actions">
                    {selectedIds.size > 0 && hasPermission('testcases.update', ['ADM', 'ADMIN', 'QA', 'GERENCIAL', 'TRIAL', 'UNITARIO']) && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowMoveModal(true)}
                      >
                        Mover para pasta
                      </button>
                    )}
                    {selectedIds.size > 0 && hasPermission('testcases.create', ['ADM', 'ADMIN', 'QA', 'TRIAL', 'GERENCIAL']) && (
                      <button
                        className="btn btn-secondary"
                        onClick={duplicateSelectedCases}
                      >
                        Duplicar
                      </button>
                    )}
                    {selectedIds.size > 0 && canEditCase && (
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={archiveSelectedCases}
                      >
                        {caseArchivedFilter === 'archived' ? '↩️ Desarquivar' : '🗄️ Arquivar'}
                      </button>
                    )}
                    {selectedIds.size > 0 && canRequestDeleteCases && (
                      <button
                        className="btn btn-danger"
                        type="button"
                        onClick={openDeleteConfirm}
                      >
                        Excluir
                      </button>
                    )}
                    {hasPermission('testcases.read', ['ADM', 'ADMIN', 'QA', 'TRIAL', 'GERENCIAL', 'UNITARIO']) && (
                      <button className="btn btn-secondary" onClick={handleExportCsv}>
                        📥 Exportar CSV
                      </button>
                    )}
                    {hasPermission('testcases.create', ['ADM', 'ADMIN', 'QA', 'TRIAL', 'GERENCIAL']) && (
                      <label
                        className="btn btn-secondary"
                        style={{ cursor: isImportingCsv ? 'wait' : 'pointer', marginBottom: 0 }}
                      >
                        📤 Importar CSV
                        <input
                          type="file"
                          accept=".csv"
                          style={{ display: 'none' }}
                          onChange={handleImportCsv}
                          disabled={isImportingCsv}
                        />
                      </label>
                    )}
                    {(hasPermission('testcases.create', ['ADM', 'ADMIN', 'QA', 'TRIAL', 'GERENCIAL']) || tutorial?.isTutorialMode) && (
                      <button
                        className="btn btn-primary"
                        onClick={handleNewCase}
                        data-tour="add-caso-teste"
                      >
                        Adicionar caso de testes
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isImportingCsv && (
              <div className="import-loading-backdrop" role="status" aria-live="polite">
                <div className="import-loading-modal">
                  <div className="import-loading-spinner" aria-hidden="true"></div>
                  <h3>Importando CSV</h3>
                  <p>Estamos processando os casos de teste. Aguarde um momento.</p>
                </div>
              </div>
            )}

            {importSummary && !isImportingCsv && (
              <div className="import-summary-backdrop" role="dialog" aria-modal="true" aria-labelledby="import-summary-title">
                <div className="import-summary-modal">
                  <div className="import-summary-header">
                    <div>
                      <h3 id="import-summary-title">Resumo da importação</h3>
                      <p>
                        {importSummary.created} de {importSummary.total} caso(s) foram importados.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="import-summary-close"
                      onClick={() => setImportSummary(null)}
                      aria-label="Fechar resumo da importação"
                    >
                      ×
                    </button>
                  </div>

                  <div className="import-summary-stats">
                    <div className="import-summary-stat success">
                      <span>{importSummary.created}</span>
                      <small>Importados</small>
                    </div>
                    <div className="import-summary-stat failed">
                      <span>{importSummary.failed}</span>
                      <small>Não importados</small>
                    </div>
                    <div className="import-summary-stat total">
                      <span>{importSummary.total}</span>
                      <small>Total lido</small>
                    </div>
                  </div>

                  {importSummary.errors.length > 0 ? (
                    <>
                      <div className="import-summary-section-title">Casos que não subiram</div>
                      <div className="import-summary-errors">
                        {importSummary.errors.map((item, index) => (
                          <div className="import-summary-error" key={`${item.title || 'sem-titulo'}-${index}`}>
                            <strong>{item.title || `Linha ${index + 1}`}</strong>
                            <span>{item.error || 'Erro não informado'}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="import-summary-ok">Todos os casos foram importados com sucesso.</div>
                  )}

                  <div className="import-summary-actions">
                    <button type="button" className="btn btn-primary" onClick={() => setImportSummary(null)}>
                      Entendi
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de confirmação de exclusão */}
            {showDeleteConfirm && (
              <div className="modal-overlay">
                <div className="modal">
                  <h3>Confirmar exclusão</h3>
                  <p>Tem certeza que deseja excluir {deletePendingIds.length} caso(s) de teste?</p>
                  <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={cancelDeleteConfirm}>Cancelar</button>
                    <button className="btn btn-danger" onClick={confirmDeleteSelectedCases}>Confirmar</button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de resultados detalhados */}
            {showDeleteResults && (
              <div className="modal-overlay">
                <div className="modal">
                  <h3>Resumo da exclusão</h3>
                  {deleteResults?.successes?.length > 0 && (
                    <p>Excluídos: {deleteResults.successes.length}</p>
                  )}
                  {deleteResults?.failures?.length > 0 && (
                    <div>
                      <p>Falhas: {deleteResults.failures.length}</p>
                      <ul>
                        {deleteResults.failures.map((f) => (
                          <li key={`fail-${f.id}`}>{f.id}: {f.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="modal-actions">
                    <button className="btn btn-primary" onClick={() => setShowDeleteResults(false)}>Fechar</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'cycles' && !cycleInlineView && (
              <div className="table-toolbar">
                <div className="table-toolbar-left">
                  <select
                    className="archived-filter-select"
                    value={cycleArchivedFilter}
                    onChange={(e) => setCycleArchivedFilter(e.target.value)}
                    title="Filtrar por arquivamento"
                  >
                    <option value="active">Ativos</option>
                    <option value="archived">Arquivados</option>
                  </select>
                  <span>|</span>
                  <label className="select-all">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                    Selecionar tudo
                  </label>
                  <span>|</span>
                  <span>Selecionados: {selectedIds.size}</span>
                  {cycleSort.field && (
                    <>
                      <span>|</span>
                      <button
                        type="button"
                        className="btn-link"
                        onClick={clearCycleSort}
                        title="Voltar para a ordem padrão"
                      >
                        ✕ Remover ordenação
                      </button>
                    </>
                  )}
                </div>
                <div className="table-toolbar-right">
                  <div className="table-toolbar-actions">
                    {selectedIds.size > 0 && hasPermission('executions.create', ['ADM', 'ADMIN', 'QA', 'TRIAL', 'GERENCIAL']) && (
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={duplicateSelectedCycles}
                      >
                        Duplicar ciclo
                      </button>
                    )}
                    {selectedIds.size > 0 && canEditCycle && (
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={archiveSelectedCycles}
                      >
                        {cycleArchivedFilter === 'archived' ? '↩️ Desarquivar' : '🗄️ Arquivar'}
                      </button>
                    )}
                    {selectedIds.size > 0 && canDeleteCycle && (
                      <button
                        className="btn btn-danger"
                        type="button"
                        onClick={promptDeleteSelectedCycles}
                      >
                        Excluir
                      </button>
                    )}
                    {(hasPermission('executions.create', ['ADM', 'ADMIN', 'QA', 'TRIAL', 'GERENCIAL']) || tutorial?.isTutorialMode) && (
                      <button 
                        className="btn btn-primary"
                        onClick={handleNewCycle}
                        data-tour="novo-ciclo"
                      >
                        + Novo Ciclo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {!((activeTab === 'cycles' && cycleInlineView) || (activeTab === 'cases' && caseInlineView)) && (
            <div
              className="table-scroll-control"
              ref={horizontalScrollRef}
              onScroll={handleHorizontalScroll}
              aria-label="Rolagem horizontal da tabela"
            >
              <div style={{ width: horizontalScrollWidth }} />
            </div>
          )}

          <div
            className="content-body"
            ref={contentBodyRef}
            onScroll={handleContentBodyScroll}
          >
            {activeTab === 'cases'
              ? (caseInlineView ? renderCaseInline() : renderTestCases())
              : (cycleInlineView ? renderCycleInline() : renderTestCycles())}
          </div>
        </div>
      </div>

      ) : (
        <div className="repository-content">
          <div className="main-content">
            <div className="content-body">
              <div className="empty-state">Selecione um projeto para visualizar o repositório.</div>
            </div>
          </div>
        </div>
      )}

      {/* Modais */}

      {/* Modal de confirmação para exclusão de ciclos */}
      <DeleteTestCycleModal
        isOpen={showDeleteCycleModal}
        onClose={() => setShowDeleteCycleModal(false)}
        onConfirm={deleteSelectedCycles}
        count={pendingDeletionIds.length}
        cycles={(testCycles || []).filter(c => pendingDeletionIds.includes(c.id))}
      />

      <NewFolderModal
        isOpen={showNewFolderModal}
        onClose={() => setShowNewFolderModal(false)}
        onSuccess={() => {
          fetchFolders();
          setShowNewFolderModal(false);
        }}
        parentFolder={contextFolder || selectedFolder}
        defaultProjectId={selectedProject}
        scope={activeTab}
      />

      <RenameFolderModal
        isOpen={showRenameFolderModal}
        onClose={() => setShowRenameFolderModal(false)}
        folder={contextFolder || selectedFolder}
        onSuccess={(updated) => {
          // Se a pasta renomeada é a selecionada, atualiza o estado
          if (selectedFolder && updated && selectedFolder.id === updated.id) {
            setSelectedFolder({ ...selectedFolder, name: updated.name, description: updated.description });
          }
          fetchFolders();
          setShowRenameFolderModal(false);
        }}
      />

      <DeleteFolderModal
        isOpen={showDeleteFolderModal}
        onClose={() => setShowDeleteFolderModal(false)}
        folder={contextFolder || selectedFolder}
        onSuccess={() => {
          // Se excluiu a pasta selecionada, resetar seleção
          if (selectedFolder && contextFolder && selectedFolder.id === contextFolder.id) {
            setSelectedFolder(null);
            fetchTestCases();
          }
          fetchFolders();
          setShowDeleteFolderModal(false);
        }}
      />

      <MoveTestCasesModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        folders={folders}
        selectedIds={Array.from(selectedIds)}
        onSuccess={() => {
          // Atualizar lista e árvore; limpar seleção
          fetchTestCases(currentFolderScope());
          fetchFolders();
          setSelectedIds(new Set());
          setShowMoveModal(false);
        }}
      />

      <MoveFoldersModal
        isOpen={showMoveFoldersModal}
        onClose={() => setShowMoveFoldersModal(false)}
        folders={folders}
        selectedIds={Array.from(selectedFolderIds)}
        onSuccess={() => {
          fetchFolders();
          setSelectedFolderIds(new Set());
          setShowMoveFoldersModal(false);
        }}
      />

    </div>
  );
};

export default TestRepository;
